/**
 * Ripia 認証APIサービス
 * プロダクション対応 - バックエンド統合
 *
 * @description JWT認証・Token管理・API統合サービス
 * @backend NestJS + JWT + PostgreSQL統合
 * @security 業界最高水準セキュリティ
 */

import {
  saveEncryptedToken,
  loadEncryptedToken,
  removeEncryptedToken,
  isTokenValid,
} from '../utils/crypto';
import * as Sentry from '@sentry/react';
// storageUtils imports removed - Organization dead code cleanup
import logger from '../utils/logger';
import { SENT_CUSTOM_HEADERS } from './constants/sent-custom-headers.const';
import { UnknownObject } from '../types/common.types';
import MFAService from './mfaService';
import { AuditLogService, AuditEventType } from './auditLogService';

// Multi-Tenant API Integration - backend/完全互換型
import type {
  EnhancedJWTPayload,
  TenantInfo,
  UserInfo,
  TenantContext,
  TenantPermissionActions,
  TenantResource,
  PlanFeature,
  MultiTenantApiError,
  ApiRequestConfig,
  MultiTenantLoginRequest,
  MultiTenantLoginResponse,
  TenantSwitchRequest,
  TenantSwitchResponse,
  NotificationSettings,
} from '../types/multiTenant';

// Runtime value imports
import { SecurityErrorType } from '../types/multiTenant';

// JWT統合セキュリティシステム
import { EnhancedJWTService } from '../utils/enhancedJWT';
import {
  TokenBlacklistService,
  RevocationRequest,
} from '../utils/tokenBlacklist';
import { DeviceAuthService, DeviceCharacteristics } from '../utils/deviceAuth';
import { MultiTenantStorageService } from '../utils/multiTenantStorage';

// Environment configuration with secure API URL
import env from '../config/env';

// Issue #1401: SalonBusinessType SSOT
import type { SalonBusinessType } from '../constants/businessTypes';

// Issue #2056: TransformInterceptor envelope統合アンラップ
import { unwrapResponse } from '../utils/apiResponseUtils';

// Issue #2971: shops API 実呼び出しで availableStores を取得 (mock/空配列 TODO 解消)
import { shopsControllerFindAll } from '../generated/api/shops/shops';

// API設定 - セキュアな環境変数管理
const API_CONFIG = {
  // セキュリティ強化: env.tsから安全なAPI URL取得
  BASE_URL: env.getApiConfig().baseUrl,
  ENDPOINTS: {
    // V1 エンドポイント（NestJS versioning: /api/v1/...）
    REFRESH: '/api/v1/auth/refresh',
    LOGOUT: '/api/v1/auth/logout',
    ME: '/api/v1/auth/me',
    GOOGLE: '/api/v1/auth/google',

    // メール認証エンドポイント（Phase 3追加）
    // Note: /api/v1/auth/register includes email verification functionality
    REGISTER_WITH_EMAIL_VERIFICATION: '/api/v1/auth/register',
    VERIFY_EMAIL: '/api/v1/auth/verify-email',
    RESEND_VERIFICATION_EMAIL: '/api/v1/auth/resend-verification',

    // V2 マルチテナント認証エンドポイント (Issue #282統合)
    V2_AUTH: {
      LOGIN: '/v2/auth/login',
      REFRESH: '/v2/auth/refresh',
      LOGOUT: '/v2/auth/logout',
      ME: '/v2/auth/me',
      SWITCH_TENANT: '/v2/auth/switch-tenant',
      VERIFY_TOKEN: '/v2/auth/verify-token',
      REVOKE_TOKEN: '/v2/auth/revoke-token',
      MFA_SETUP: '/v2/auth/mfa/setup',
      MFA_VERIFY: '/v2/auth/mfa/verify',
      PASSWORD_POLICY: '/v2/auth/password-policy',
      SECURITY_STATUS: '/v2/auth/security-status',
    },

    // V2 ユーザー管理エンドポイント (Enhanced Controller統合)
    V2_USERS: {
      LIST: '/v2/users',
      CREATE: '/v2/users',
      GET_BY_ID: '/v2/users/{id}',
      UPDATE: '/v2/users/{id}',
      DELETE: '/v2/users/{id}',
      GET_PERMISSIONS: '/v2/users/{id}/permissions',
      BULK_ACTION: '/v2/users/bulk-action',
      DEBUG_CONTEXT: '/v2/users/debug/tenant-context',
    },

    // V2 テナント管理エンドポイント
    V2_TENANTS: {
      LIST: '/v2/tenants',
      GET_CURRENT: '/v2/tenants/current',
      GET_BY_ID: '/v2/tenants/{id}',
      UPDATE: '/v2/tenants/{id}',
      GET_SETTINGS: '/v2/tenants/{id}/settings',
      UPDATE_SETTINGS: '/v2/tenants/{id}/settings',
      GET_ANALYTICS: '/v2/tenants/{id}/analytics',
      GET_RATE_LIMITS: '/v2/tenants/{id}/rate-limits',
    },
  },
  TIMEOUT: 10000, // 10秒タイムアウト
  RETRY_COUNT: 3,
} as const;

// Token保存キー（既存・完全保持）
const TOKEN_KEYS = {
  ACCESS_TOKEN: 'ripia_access_token',
  REFRESH_TOKEN: 'ripia_refresh_token',
  USER_DATA: 'ripia_user_data',
  // マルチテナント拡張
  TENANT_CONTEXT: 'ripia:auth:tenant_context',
  AVAILABLE_TENANTS: 'ripia_available_tenants',
  SELECTED_TENANT: 'ripia_selected_tenant',
} as const;

const LEGACY_TENANT_CONTEXT_KEYS = [
  'ripia_tenant_context',
  'salonhub_current_tenant_context',
  'salonhub_tenant_context',
] as const;

export type TenantContextRecoveryReason =
  | 'auth_required'
  | 'setup_pending'
  | 'transient'
  | 'unknown';

export type TenantContextFetchResult =
  | { ok: true; tenantContext: TenantContext }
  | {
      ok: false;
      reason: TenantContextRecoveryReason;
      status?: number;
      retryable: boolean;
    };

type ClerkSessionForTenantContext = {
  getToken: (opts?: { skipCache?: boolean }) => Promise<string | null>;
};

// LocalStorageキー移行（Issue #795 Phase 3）
// 古いキー名から新しいキー名へデータを移行
const LEGACY_KEY_MIGRATIONS: Array<{ oldKey: string; newKey: string }> = [
  // Rebrand (ADR-0026): salonhub_* → ripia_* キー移行
  { oldKey: 'salonhub_access_token', newKey: TOKEN_KEYS.ACCESS_TOKEN },
  { oldKey: 'salonhub_refresh_token', newKey: TOKEN_KEYS.REFRESH_TOKEN },
  { oldKey: 'salonhub_user_data', newKey: TOKEN_KEYS.USER_DATA },
  {
    oldKey: 'salonhub_available_tenants',
    newKey: TOKEN_KEYS.AVAILABLE_TENANTS,
  },
  { oldKey: 'salonhub_selected_tenant', newKey: TOKEN_KEYS.SELECTED_TENANT },
  // System keys
  { oldKey: 'SALONHUB_COMPLETE_SETUP_v2', newKey: 'RIPIA_COMPLETE_SETUP_v2' },
  { oldKey: 'salonhub_remember_me', newKey: 'ripia_remember_me' },
];

const isEncryptedTokenStorageValue = (rawValue: string): boolean => {
  try {
    const parsed = JSON.parse(rawValue) as Record<string, unknown>;
    return (
      typeof parsed.encrypted === 'string' &&
      typeof parsed.iv === 'string' &&
      typeof parsed.salt === 'string' &&
      typeof parsed.hmac === 'string'
    );
  } catch {
    return false;
  }
};

const getLegacyTenantContextRawValue = (
  legacyKey: string
): string | null => {
  return localStorage.getItem(legacyKey) ?? sessionStorage.getItem(legacyKey);
};

const getSafeErrorExtra = (
  error: unknown
): { errorName: string; errorMessage: string } => {
  if (error instanceof Error) {
    return {
      errorName: error.name,
      errorMessage: error.message.slice(0, 200),
    };
  }

  return {
    errorName: typeof error,
    errorMessage: String(error).slice(0, 200),
  };
};

const getSafeErrorName = (error: unknown): string => {
  return error instanceof Error ? error.name : typeof error;
};

const createTenantContextFetchFailure = (
  reason: TenantContextRecoveryReason,
  status?: number
): TenantContextFetchResult => ({
  ok: false,
  reason,
  retryable: reason !== 'auth_required',
  ...(status === undefined ? {} : { status }),
});

const migrateLegacyTenantContextStorage = (): void => {
  try {
    if (typeof window === 'undefined') return;
    if (
      localStorage.getItem(TOKEN_KEYS.TENANT_CONTEXT) ||
      sessionStorage.getItem(TOKEN_KEYS.TENANT_CONTEXT)
    ) {
      return;
    }

    for (const legacyKey of LEGACY_TENANT_CONTEXT_KEYS) {
      const rawValue = getLegacyTenantContextRawValue(legacyKey);
      if (!rawValue || !isEncryptedTokenStorageValue(rawValue)) {
        continue;
      }

      const tenantContextData = loadEncryptedToken(legacyKey);
      if (!tenantContextData) {
        Sentry.captureMessage(
          'AuthService: encrypted legacy tenantContext decrypt failed',
          {
            level: 'warning',
            extra: {
              legacyKey,
            },
          }
        );
        continue;
      }

      saveEncryptedToken(tenantContextData, TOKEN_KEYS.TENANT_CONTEXT);
      removeEncryptedToken(legacyKey);
      logger.info(
        `[Issue #3582] Migrated encrypted tenantContext key: ${legacyKey} -> ${TOKEN_KEYS.TENANT_CONTEXT}`
      );
      return;
    }
  } catch (error) {
    Sentry.captureMessage('AuthService: tenantContext legacy migration failed', {
      level: 'warning',
      extra: getSafeErrorExtra(error),
    });
  }
};

/**
 * 既存ユーザーのLocalStorageキーを移行
 * アプリ起動時に一度だけ実行される
 */
const migrateLocalStorageKeys = (): void => {
  const migrationFlag = 'ripia_key_migration_v3_completed';

  // 既に移行済みの場合はスキップ
  if (localStorage.getItem(migrationFlag)) {
    return;
  }

  try {
    for (const { oldKey, newKey } of LEGACY_KEY_MIGRATIONS) {
      const oldData = localStorage.getItem(oldKey);
      const newData = localStorage.getItem(newKey);

      // 古いキーにデータがあり、新しいキーにデータがない場合のみ移行
      if (oldData && !newData) {
        localStorage.setItem(newKey, oldData);
        logger.info(
          `[Issue #795] Migrated LocalStorage key: ${oldKey} -> ${newKey}`
        );
      }
    }

    migrateLegacyTenantContextStorage();

    // 移行完了フラグを設定
    localStorage.setItem(migrationFlag, new Date().toISOString());
    logger.info('[Issue #795] LocalStorage key migration completed');
  } catch (error) {
    logger.error('[Issue #795] LocalStorage key migration failed:', error);
  }
};

// アプリ起動時に移行を実行
migrateLocalStorageKeys();

// マルチテナントデータ型定義 (通知設定・権限設定は multiTenant.ts に集約済み)

// マルチテナントデータ型定義
interface MultiTenantData extends UnknownObject {
  tenantId?: string;
  additionalInfo?: UnknownObject;
}

// Legacy User Type for backward compatibility
export interface User {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'manager' | 'staff';
  storeId?: string;
  // Issue #2059: organizationId → tenantId 統一
  tenantId?: string;
  permissions?: string[];
  createdAt?: string;
  lastLoginAt?: string;
  // MFA関連プロパティ
  mfaEnabled?: boolean;
  emailVerified?: boolean;
  // Preview環境専用プロパティ
  isPreview?: boolean;
  // Organization management properties
  tenantRole?: 'owner' | 'admin' | 'member' | 'viewer' | 'staff' | 'manager';
}

// Legacy Types (maintained for backward compatibility with existing code)
export type TenantRole = 'owner' | 'manager' | 'staff';
export type PlanType = 'light' | 'standard' | 'premium' | 'premium';

export interface TenantStore {
  id: string;
  name: string;
  code: string;
}

// Multi-Tenant User (combines legacy User with new TenantContext)
export interface MultiTenantUser extends Omit<User, 'permissions'> {
  tenantContext?: TenantContext;
  availableTenants: TenantInfo[];
  // UserInfo compatibility properties
  tenantRole: EnhancedJWTPayload['tenant_role'];
  isActive: boolean;
  // Override permissions to match UserInfo requirements
  permissions: Record<string, TenantPermissionActions>;
}

// JWT Payload型定義（既存・完全保持 + エンタープライズ拡張）
export interface JWTPayload {
  sub: string; // User ID
  email: string; // ユーザーメール
  role: 'owner' | 'staff'; // 権限
  storeId?: string; // 店舗ID
  permissions: string[]; // 詳細権限
  iat: number; // 発行時刻
  exp: number; // 有効期限
  // エンタープライズセキュリティ拡張
  jti?: string; // JWT ID（無効化管理用）
  device_fp?: string; // デバイスフィンガープリント
  session_id?: string; // セッションID
  security_level?: 'standard' | 'high' | 'premium';
  // MFA（多要素認証）情報
  mfa_enabled?: boolean; // MFAが有効かどうか
  mfa_verified?: boolean; // MFA認証済みかどうか
  mfa_required?: boolean; // MFA認証が必須かどうか
  temp_auth?: boolean; // 一時認証フラグ（MFA前の状態）
}

// API応答型定義（既存・完全保持）
export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface LoginResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface RefreshResponse {
  accessToken: string;
  expiresIn: number;
}

// ====================================
// MFA（多要素認証）関連型定義
// ====================================

export interface MFAEnableRequest {
  userId: string;
  secretKey: string;
  verificationCode: string;
  backupCodes: string[];
}

export interface MFAEnableResponse {
  success: boolean;
  message: string;
  backupCodesCount: number;
}

export interface MFADisableRequest {
  userId: string;
  currentPassword: string;
  verificationCode?: string;
  backupCode?: string;
}

export interface MFADisableResponse {
  success: boolean;
  message: string;
}

export interface MFAVerifyRequest {
  tempToken: string;
  code: string;
  isBackupCode?: boolean;
}

export interface MFAVerifyResponse {
  success: boolean;
  message: string;
  accessToken?: string;
  refreshToken?: string;
  user?: User;
  backupCodesRemaining?: number;
}

export interface MFAStatusRequest {
  userId: string;
}

export interface MFAStatusResponse {
  isEnabled: boolean;
  backupCodesRemaining: number;
  lastUsed?: string;
  setupDate?: string;
}

export interface MFAGenerateBackupCodesRequest {
  userId: string;
  currentPassword: string;
  verificationCode?: string;
  backupCode?: string;
}

export interface MFAGenerateBackupCodesResponse {
  success: boolean;
  backupCodes: string[];
  message: string;
}

export interface TempAuthToken {
  tempToken: string;
  expiresAt: number;
  email: string;
  userId: string;
  mfaRequired: boolean;
}

// ========================
// メール認証関連の型定義
// ========================

export interface RegisterWithEmailVerificationRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  // マルチテナント対応フィールド
  shopName?: string;
  businessType?: SalonBusinessType;
  selectedPlan?: PlanType;
}

export interface RegisterWithEmailVerificationResponse {
  success: boolean;
  message: string;
  userId: string;
  verificationRequired: boolean;
  token?: string; // Issue #327 Phase 2: JWT token for Checkout Session API
}

export interface VerifyEmailRequest {
  userId: string;
  verificationCode: string;
}

export interface VerifyEmailResponse {
  success: boolean;
  message: string;
  shouldRedirectToOrganizationCreation: boolean;
  // オプション: メール認証後にトークンが返される場合
  accessToken?: string;
  refreshToken?: string;
  user?: User;
  checkoutUrl?: string; // ← Issue #327 Phase 2-A修正: Stripe Checkout URL追加
}

export interface ResendVerificationEmailRequest {
  userId: string;
}

// エンタープライズセキュリティ拡張型定義
// ========================
// 組織作成関連の型定義 → auth/types.ts に統一 (Issue #2299)
// re-export は L5128 の `export type * from './auth/types'` で包括的にカバー

export interface EnhancedLoginRequest extends LoginRequest {
  deviceCharacteristics?: DeviceCharacteristics;
  securityLevel?: 'standard' | 'high' | 'premium';
  rememberDevice?: boolean;
}

export interface EnhancedLoginResponse extends LoginResponse {
  jti: string;
  deviceFingerprint?: string;
  securityLevel: 'standard' | 'high' | 'premium';
  isDeviceTrusted: boolean;
}

export interface SecurityAuditInfo {
  tokenId: string;
  issuedAt: number;
  expiresAt: number;
  deviceFingerprint?: string;
  lastActivity: number;
  securityLevel: string;
}

export interface DeviceManagementInfo {
  fingerprint: string;
  trustScore: number;
  lastUsed: number;
  isBlocked: boolean;
  deviceName?: string;
}

// エラー型定義
export class AuthError extends Error {
  constructor(
    message: string,
    public code:
      | 'INVALID_CREDENTIALS'
      | 'TOKEN_EXPIRED'
      | 'NETWORK_ERROR'
      | 'UNAUTHORIZED'
      | 'VALIDATION_ERROR'
      | 'SERVER_ERROR'
      | 'TOKEN_REFRESHED'
      | 'SESSION_EXPIRED'
      | 'AUTHENTICATION_FAILED'
      | 'AUTHORIZATION_FAILED'
      | 'NOT_FOUND'
      | 'CONFLICT'
      | 'RATE_LIMIT_EXCEEDED'
      | 'SERVICE_UNAVAILABLE'
      | 'REGISTRATION_FAILED'
      | 'VERIFICATION_FAILED'
      | 'RESEND_FAILED'
      | 'VERIFICATION_NOT_FOUND'
      | 'VERIFICATION_EXPIRED'
      | 'INVALID_VERIFICATION_CODE'
      | 'AUTHENTICATION_REQUIRED'
      | 'ORGANIZATION_CREATION_FAILED'
      | 'DEFAULT_TENANT_CREATION_FAILED'
      | 'ORGANIZATION_NOT_FOUND'
      | 'ORGANIZATION_ACCESS_DENIED'
      | 'CONTEXT_SETTING_FAILED'
      | 'MFA_ENABLE_FAILED'
      | 'MFA_DISABLE_FAILED'
      | 'MFA_VERIFICATION_FAILED'
      | 'MFA_STATUS_FAILED'
      | 'BACKUP_CODES_GENERATION_FAILED'
      | 'TEMP_TOKEN_EXPIRED'
      | 'INVALID_MFA_CODE'
      | 'POPUP_BLOCKED'
      | 'AUTH_ERROR'
      | 'TIMEOUT',
    public status?: number
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * APIリクエストユーティリティ
 * 🔥 FIX Issue #1179: Clerk認証対応
 */
class ApiClient {
  /**
   * Clerkトークンを取得（VITE_USE_REAL_API時はClerk優先）
   * フォールバックとしてLocalStorageトークンも確認
   */
  private static async getAuthToken(): Promise<string | null> {
    // 1. Clerk SDKからトークン取得を試行
    try {
      const clerk = (
        window as unknown as {
          Clerk?: { session?: { getToken: () => Promise<string | null> } };
        }
      ).Clerk;
      if (clerk?.session) {
        const clerkToken = await clerk.session.getToken();
        if (clerkToken) {
          return clerkToken;
        }
      }
    } catch (error) {
      logger.warn(
        '[ApiClient] Clerk token retrieval failed, falling back to localStorage:',
        error
      );
    }

    // 2. フォールバック: LocalStorageトークン（レガシー互換）
    const localToken = loadEncryptedToken(TOKEN_KEYS.ACCESS_TOKEN);
    return localToken || null;
  }

  public static async fetchWithTimeout(
    url: string,
    options: RequestInit,
    timeout: number = API_CONFIG.TIMEOUT
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new AuthError(
          'リクエストがタイムアウトしました',
          'NETWORK_ERROR'
        );
      }
      throw error;
    }
  }

  public static async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch((e: unknown) => {
        logger.warn(
          '[AuthService.handleResponse] Failed to parse response JSON:',
          e
        );
        return { message: 'Response parse error' };
      });

      switch (response.status) {
        case 401:
          throw new AuthError(
            errorData.message || '認証が必要です',
            'UNAUTHORIZED',
            401
          );
        case 400:
          throw new AuthError(
            errorData.message || '不正なリクエストです',
            'VALIDATION_ERROR',
            400
          );
        case 403:
          throw new AuthError('アクセスが拒否されました', 'UNAUTHORIZED', 403);
        case 500:
          throw new AuthError(
            'サーバーエラーが発生しました',
            'SERVER_ERROR',
            500
          );
        default:
          throw new AuthError(
            errorData.message || `HTTP ${response.status} エラー`,
            'NETWORK_ERROR',
            response.status
          );
      }
    }

    return response.json();
  }

  static async post<T>(endpoint: string, data: object): Promise<T> {
    const url = `${API_CONFIG.BASE_URL}${endpoint}`;
    const token = await this.getAuthToken();

    const response = await this.fetchWithTimeout(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(data),
    });

    return this.handleResponse<T>(response);
  }

  static async get<T>(endpoint: string): Promise<T> {
    const url = `${API_CONFIG.BASE_URL}${endpoint}`;
    const token = await this.getAuthToken();

    const response = await this.fetchWithTimeout(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    return this.handleResponse<T>(response);
  }
}

/**
 * 認証サービスクラス
 */
class AuthService {
  /**
   * トークン更新 - エンタープライズセキュリティ統合
   */
  static async refreshToken(): Promise<RefreshResponse> {
    // 既存の基本リフレッシュを実行
    return this.refreshTokenBasic();
  }

  /**
   * エンタープライズセキュアトークン更新
   */
  static async refreshTokenSecure(): Promise<
    RefreshResponse & { jti: string; deviceFingerprint?: string }
  > {
    try {
      const refreshToken = loadEncryptedToken(TOKEN_KEYS.REFRESH_TOKEN);
      const storedDeviceFingerprint = loadEncryptedToken(
        'ripia_device_fingerprint'
      );

      if (!refreshToken) {
        throw new AuthError(
          'リフレッシュトークンが見つかりません',
          'TOKEN_EXPIRED'
        );
      }

      // 現在のデバイス特性収集
      const currentCharacteristics =
        DeviceAuthService.collectDeviceCharacteristics();
      const currentFingerprint = DeviceAuthService.generateAdvancedFingerprint(
        currentCharacteristics
      );

      // デバイスフィンガープリント検証
      if (
        storedDeviceFingerprint &&
        storedDeviceFingerprint !== currentFingerprint
      ) {
        logger.warn('デバイスフィンガープリント不整合検出');
        // セキュリティレベルに応じて処理を決定
        const securityLevel =
          loadEncryptedToken('ripia_security_level') || 'standard';
        if (securityLevel === 'premium') {
          throw new AuthError('デバイス認証に失敗しました', 'UNAUTHORIZED');
        }
      }

      // エンタープライズJWTローテーション実行
      const rotationResult =
        await EnhancedJWTService.refreshToken(refreshToken);

      if (!rotationResult.success) {
        throw new AuthError('トークンの更新に失敗しました', 'TOKEN_EXPIRED');
      }

      // 新しいトークン保存
      this.saveNewAccessToken(rotationResult.tokens.accessToken);
      if (rotationResult.tokens.refreshToken) {
        saveEncryptedToken(
          rotationResult.tokens.refreshToken,
          TOKEN_KEYS.REFRESH_TOKEN
        );
      }

      return {
        accessToken: rotationResult.tokens.accessToken,
        expiresIn: 24 * 60 * 60,
        jti: rotationResult.jti,
        deviceFingerprint: currentFingerprint,
      };
    } catch (error) {
      logger.error('エンタープライズトークン更新エラー:', error);
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError(
        'セキュアトークン更新に失敗しました',
        'TOKEN_EXPIRED'
      );
    }
  }

  /**
   * 基本トークン更新（既存機能・完全保持）
   */
  private static async refreshTokenBasic(): Promise<RefreshResponse> {
    try {
      const refreshToken = loadEncryptedToken(TOKEN_KEYS.REFRESH_TOKEN);
      if (!refreshToken) {
        throw new AuthError(
          'リフレッシュトークンが見つかりません',
          'TOKEN_EXPIRED'
        );
      }

      if (import.meta.env?.VITE_USE_REAL_API === 'true') {
        const response = await ApiClient.post<RefreshResponse>(
          API_CONFIG.ENDPOINTS.REFRESH,
          {
            refreshToken,
          }
        );

        // 新しいaccessTokenを保存
        saveEncryptedToken(response.accessToken, TOKEN_KEYS.ACCESS_TOKEN);
        logger.log('✅ AccessToken更新完了');

        return response;
      }

      // モック実装
      return new Promise((resolve) => {
        setTimeout(() => {
          const userData = loadEncryptedToken(TOKEN_KEYS.USER_DATA);
          const user = userData ? JSON.parse(userData) : null;

          resolve({
            accessToken: this.generateMockJWT(user || { id: 'mock-user' }),
            expiresIn: 24 * 60 * 60,
          });
        }, 500);
      });
    } catch (error) {
      logger.error('トークン更新エラー:', error);
      throw new AuthError('トークンの更新に失敗しました', 'TOKEN_EXPIRED');
    }
  }

  /**
   * ログアウト（エンタープライズセキュリティ統合）
   */
  static async logout(): Promise<void> {
    try {
      // 現在のユーザー情報を取得（ログ記録用）
      const currentUser = await this.getCurrentUser();

      // セキュアログアウト実行
      await this.logoutSecure();

      // 監査ログ記録
      if (currentUser) {
        await AuditLogService.log(AuditEventType.LOGOUT, {
          userId: currentUser.id,
          userEmail: currentUser.email,
          success: true,
        });
      }
    } catch (error) {
      logger.error('ログアウトエラー:', error);
      // エラーが発生してもローカルトークンは削除
      this.clearAllTokens();
    }
  }

  /**
   * エンタープライズセキュアログアウト
   */
  static async logoutSecure(): Promise<void> {
    try {
      const refreshToken = loadEncryptedToken(TOKEN_KEYS.REFRESH_TOKEN);
      const jti = loadEncryptedToken('ripia_jwt_jti');
      const currentUser = await this.getCurrentUser();

      // 1. トークンブラックリスト登録
      if (jti && currentUser) {
        const revocationRequest: RevocationRequest = {
          jti,
          userId: currentUser.id,
          reason: 'logout',
        };
        await TokenBlacklistService.revokeToken(revocationRequest);
      }

      // 2. サーバーサイドログアウト
      if (import.meta.env?.VITE_USE_REAL_API === 'true') {
        if (refreshToken) {
          await ApiClient.post(API_CONFIG.ENDPOINTS.LOGOUT, { refreshToken });
        }
      }

      // 3. 全ローカルトークン削除
      this.clearAllTokens();

      logger.info('セキュアログアウトが完了しました');
    } catch (error) {
      logger.error('セキュアログアウトエラー:', error);
      // エラーが発生してもローカルトークンは削除
      this.clearAllTokens();
    }
  }

  /**
   * ユーザー情報取得
   */
  static async getCurrentUser(): Promise<User | null> {
    try {
      if (import.meta.env?.VITE_USE_REAL_API === 'true') {
        return await ApiClient.get<User>(API_CONFIG.ENDPOINTS.ME);
      }

      // モック実装 - 暗号化されたトークンを復号化して読み取り
      const userData = loadEncryptedToken(TOKEN_KEYS.USER_DATA);
      if (!userData) {
        // 旧形式のキーもチェック（後方互換性）
        const oldUserData = localStorage.getItem('userData');
        if (oldUserData) {
          try {
            const user = JSON.parse(oldUserData);
            // 新形式で保存し直す
            localStorage.setItem(TOKEN_KEYS.USER_DATA, JSON.stringify(user));
            localStorage.removeItem('userData');
            return user;
          } catch (e) {
            logger.error('旧形式ユーザーデータのパースエラー:', e);
          }
        }
        return null;
      }

      const user = JSON.parse(userData);
      return user;
    } catch (error) {
      logger.error('ユーザー情報取得エラー:', error);
      return null;
    }
  }

  // メール認証システム
  // ========================

  /**
   * メール認証付き新規登録
   */
  static async registerWithEmailVerification(
    request: RegisterWithEmailVerificationRequest
  ): Promise<RegisterWithEmailVerificationResponse> {
    try {
      logger.log('📝 メール認証付き新規登録開始:', request.email);

      if (import.meta.env?.VITE_USE_REAL_API === 'true') {
        // 実際のAPI呼び出し（Phase 4で実装予定）
        const response =
          await ApiClient.post<RegisterWithEmailVerificationResponse>(
            API_CONFIG.ENDPOINTS.REGISTER_WITH_EMAIL_VERIFICATION,
            request
          );
        return response;
      } else {
        // モック実装：仮ユーザーID生成
        const mockUserId = `user_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;

        // 2秒遅延でリアルなAPI体験
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // モック認証コード生成（開発用ログ出力）
        const mockVerificationCode = Math.floor(
          100000 + Math.random() * 900000
        ).toString();
        logger.log('🔐 [MOCK] 認証コード生成:', mockVerificationCode);
        logger.log('👤 [MOCK] ユーザーID:', mockUserId);

        // LocalStorageに仮認証情報を保存（開発用）
        localStorage.setItem(
          `verification_${mockUserId}`,
          JSON.stringify({
            code: mockVerificationCode,
            email: request.email,
            userData: request,
            expires: Date.now() + 15 * 60 * 1000, // 15分後
          })
        );

        return {
          success: true,
          message: 'メール認証コードを送信しました。メールをご確認ください。',
          userId: mockUserId,
          verificationRequired: true,
        };
      }
    } catch (error) {
      logger.error('❌ メール認証付き新規登録エラー:', error);

      if (error instanceof AuthError) {
        throw error;
      }

      throw new AuthError(
        '新規登録に失敗しました。別のメールアドレスをお試しください。',
        'REGISTRATION_FAILED'
      );
    }
  }

  /**
   * メール認証の実行
   */
  static async verifyEmail(
    request: VerifyEmailRequest
  ): Promise<VerifyEmailResponse> {
    try {
      logger.log('🔐 メール認証開始:', request.userId);

      if (import.meta.env?.VITE_USE_REAL_API === 'true') {
        // 実際のAPI呼び出し
        const response = (await ApiClient.post(
          API_CONFIG.ENDPOINTS.VERIFY_EMAIL,
          request
        )) as VerifyEmailResponse;

        // トークンが返されている場合は保存（モック実装と同じ処理）
        if (response.accessToken && response.refreshToken && response.user) {
          const loginResponse: LoginResponse = {
            user: response.user as User,
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            expiresIn: 3600, // デフォルト値（1時間）
          };
          this.saveTokens(loginResponse);
          logger.log(
            '✅ メール認証成功 - トークン保存完了:',
            response.user.email
          );
        } else {
          logger.warn('⚠️ メール認証レスポンスにトークンが含まれていません');
        }

        return response;
      } else {
        // モック実装：LocalStorageから認証情報取得
        const verificationData = localStorage.getItem(
          `verification_${request.userId}`
        );

        if (!verificationData) {
          throw new AuthError(
            '認証情報が見つかりません。もう一度新規登録を行ってください。',
            'VERIFICATION_NOT_FOUND'
          );
        }

        const parsedData = JSON.parse(verificationData);

        // 有効期限チェック
        if (Date.now() > parsedData.expires) {
          localStorage.removeItem(`verification_${request.userId}`);
          throw new AuthError(
            '認証コードの有効期限が切れています。もう一度新規登録を行ってください。',
            'VERIFICATION_EXPIRED'
          );
        }

        // 認証コードチェック
        if (parsedData.code !== request.verificationCode) {
          throw new AuthError(
            '認証コードが正しくありません。',
            'INVALID_VERIFICATION_CODE'
          );
        }

        // 1秒遅延でリアルなAPI体験
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // 認証成功：LocalStorageから認証情報削除
        localStorage.removeItem(`verification_${request.userId}`);

        // モックユーザーデータ作成
        const userData = parsedData.userData;
        const mockUser: User = {
          id: request.userId,
          email: userData.email,
          name: `${userData.firstName} ${userData.lastName}`,
          role: 'owner',
          createdAt: new Date().toISOString(),
        };

        // 自動ログイン実行（認証成功したユーザー）
        const mockLoginResponse: LoginResponse = {
          user: mockUser,
          accessToken: `mock_jwt_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`,
          refreshToken: `refresh_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`,
          expiresIn: 3600,
        };

        // トークン保存
        this.saveTokens(mockLoginResponse);

        logger.log('✅ メール認証成功:', userData.email);

        return {
          success: true,
          message: 'メール認証が完了しました。',
          shouldRedirectToOrganizationCreation: true,
        };
      }
    } catch (error) {
      logger.error('❌ メール認証エラー:', error);

      if (error instanceof AuthError) {
        throw error;
      }

      throw new AuthError('認証に失敗しました。', 'VERIFICATION_FAILED');
    }
  }

  /**
   * 認証メール再送信
   */
  static async resendVerificationEmail(
    request: ResendVerificationEmailRequest
  ): Promise<void> {
    try {
      logger.log('📧 認証メール再送信開始:', request.userId);

      if (import.meta.env?.VITE_USE_REAL_API === 'true') {
        // 実際のAPI呼び出し（Phase 4で実装予定）
        await ApiClient.post(
          API_CONFIG.ENDPOINTS.RESEND_VERIFICATION_EMAIL,
          request
        );
      } else {
        // モック実装：新しい認証コード生成
        const verificationData = localStorage.getItem(
          `verification_${request.userId}`
        );

        if (!verificationData) {
          throw new AuthError(
            '認証情報が見つかりません。もう一度新規登録を行ってください。',
            'VERIFICATION_NOT_FOUND'
          );
        }

        const parsedData = JSON.parse(verificationData);

        // 新しい認証コード生成
        const newVerificationCode = Math.floor(
          100000 + Math.random() * 900000
        ).toString();
        logger.log('🔐 [MOCK] 新しい認証コード:', newVerificationCode);

        // 有効期限更新
        parsedData.code = newVerificationCode;
        parsedData.expires = Date.now() + 15 * 60 * 1000; // 15分後

        localStorage.setItem(
          `verification_${request.userId}`,
          JSON.stringify(parsedData)
        );

        // 2秒遅延でリアルなAPI体験
        await new Promise((resolve) => setTimeout(resolve, 2000));

        logger.log('✅ 認証メール再送信完了');
      }
    } catch (error) {
      logger.error('❌ 認証メール再送信エラー:', error);

      if (error instanceof AuthError) {
        throw error;
      }

      throw new AuthError(
        '認証メールの再送信に失敗しました。',
        'RESEND_FAILED'
      );
    }
  }

  /**
   * トークン保存
   */
  static saveTokens(response: LoginResponse, rememberMe?: boolean): void {
    // rememberMe=trueの場合はLocalStorage（7日間）、falseの場合はSessionStorage（ブラウザセッション終了まで）
    const storageType = rememberMe ? 'localStorage' : 'sessionStorage';

    saveEncryptedToken(
      response.accessToken,
      TOKEN_KEYS.ACCESS_TOKEN,
      storageType
    );
    saveEncryptedToken(
      response.refreshToken,
      TOKEN_KEYS.REFRESH_TOKEN,
      storageType
    );
    saveEncryptedToken(
      JSON.stringify(response.user),
      TOKEN_KEYS.USER_DATA,
      storageType
    );

    // rememberMe設定も保存（次回ログイン時のデフォルト値として使用）
    if (rememberMe !== undefined) {
      localStorage.setItem('ripia_remember_me', rememberMe.toString());
    }
  }

  /**
   * 新しいアクセストークン保存
   */
  static saveNewAccessToken(accessToken: string): void {
    saveEncryptedToken(accessToken, TOKEN_KEYS.ACCESS_TOKEN);
  }

  /**
   * トークンクリア
   */
  static clearTokens(): void {
    removeEncryptedToken(TOKEN_KEYS.ACCESS_TOKEN);
    removeEncryptedToken(TOKEN_KEYS.REFRESH_TOKEN);
    removeEncryptedToken(TOKEN_KEYS.USER_DATA);
  }

  /**
   * 認証状態チェック - エンタープライズセキュリティ統合
   */
  static isAuthenticated(): boolean {
    return this.isAuthenticatedBasic();
  }

  /**
   * エンタープライズセキュア認証状態チェック
   */
  static async isAuthenticatedSecure(): Promise<boolean> {
    try {
      const token = loadEncryptedToken(TOKEN_KEYS.ACCESS_TOKEN);
      if (!token) return false;

      // 1. 基本トークン有効性チェック
      if (!isTokenValid(token)) {
        return false;
      }

      // 2. ブラックリストチェック
      const jti = loadEncryptedToken('ripia_jwt_jti');
      if (jti) {
        const isRevoked = await TokenBlacklistService.isTokenRevoked(jti);
        if (isRevoked) {
          logger.warn('無効化されたトークンが検出されました');
          this.clearAllTokens();
          return false;
        }
      }

      // 3. デバイス認証チェック
      const storedFingerprint = loadEncryptedToken('ripia_device_fingerprint');
      const securityLevel =
        loadEncryptedToken('ripia_security_level') || 'standard';

      if (securityLevel === 'premium' && storedFingerprint) {
        const currentCharacteristics =
          DeviceAuthService.collectDeviceCharacteristics();
        const currentFingerprint =
          DeviceAuthService.generateAdvancedFingerprint(currentCharacteristics);

        if (storedFingerprint !== currentFingerprint) {
          logger.warn('デバイスフィンガープリント不整合を検出');
          return false;
        }
      }

      return true;
    } catch (error) {
      logger.error('セキュア認証チェックエラー:', error);
      return false;
    }
  }

  /**
   * 基本認証状態チェック（既存機能・完全保持）
   */
  private static isAuthenticatedBasic(): boolean {
    const token = loadEncryptedToken(TOKEN_KEYS.ACCESS_TOKEN);
    return token ? isTokenValid(token) : false;
  }

  /**
   * 権限チェック
   */
  static hasPermission(permission: string): boolean {
    try {
      const userData = loadEncryptedToken(TOKEN_KEYS.USER_DATA);
      if (!userData) return false;

      const user: User = JSON.parse(userData);

      // Owner権限は全アクセス可能
      if (user.role === 'owner') return true;

      // 特定権限チェック
      return (
        user.permissions?.includes(permission) ||
        user.permissions?.includes('*') ||
        false
      );
    } catch (error) {
      logger.error('権限チェックエラー:', error);
      return false;
    }
  }

  /**
   * モックJWT生成（開発用・既存機能完全保持）
   */
  public static generateMockJWT(user: Partial<User>): string {
    const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
    const payload = btoa(
      JSON.stringify({
        sub: user.id,
        email: user.email,
        role: user.role,
        storeId: user.storeId,
        permissions: user.permissions || [],
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + 24 * 60 * 60, // 24時間
      })
    );
    const signature = btoa(`mock-signature-${Date.now()}`);

    return `${header}.${payload}.${signature}`;
  }

  // ================================================================
  // マルチテナント認証システム拡張機能
  // ================================================================

  /**
   * マルチテナント対応ログイン
   */
  static async loginWithTenant(
    credentials: MultiTenantLoginRequest
  ): Promise<MultiTenantLoginResponse> {
    try {
      // Issue #3138: bcrypt login pathway removed. Clerk handles authentication.
      // Production callers must use Clerk SignIn; this mock only serves dev/test flows.
      if (!import.meta.env.DEV) {
        throw new AuthError(
          'loginWithTenant is deprecated; use Clerk authentication.',
          'SERVER_ERROR'
        );
      }
      if (!credentials.email || !credentials.password) {
        throw new AuthError(
          'メールアドレスとパスワードを入力してください',
          'VALIDATION_ERROR'
        );
      }
      const mockUser: User = {
        id: `user-${Date.now()}`,
        email: credentials.email,
        name: credentials.email.split('@')[0],
        role: credentials.email.includes('owner') ? 'owner' : 'staff',
        storeId: '',
        permissions: [],
        createdAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
      };
      const basicLoginResponse: LoginResponse = {
        user: mockUser,
        accessToken: this.generateMockJWT(mockUser),
        refreshToken: `refresh_${Date.now()}_${Math.random().toString(36)}`,
        expiresIn: 24 * 60 * 60,
      };

      // テナント情報を取得（モック実装）
      const availableTenants = await this.getMockTenantList(
        basicLoginResponse.user.id
      );

      let selectedTenant: TenantContext | undefined;

      // テナントIDが指定されている場合は選択
      if (credentials.tenantId) {
        const targetTenant = availableTenants.find(
          (t) => t.id === credentials.tenantId
        );
        if (!targetTenant) {
          throw new AuthError(
            '指定されたテナントにアクセスできません',
            'UNAUTHORIZED'
          );
        }
        selectedTenant = await this.createTenantContext(
          targetTenant,
          basicLoginResponse.user.id
        );
      }

      // MultiTenantUserを作成
      const multiTenantUser: MultiTenantUser = {
        ...basicLoginResponse.user,
        tenantContext: selectedTenant,
        availableTenants: availableTenants,
        tenantRole: selectedTenant?.userRole || 'staff',
        isActive: true,
        permissions:
          typeof selectedTenant?.permissions === 'object' &&
          !Array.isArray(selectedTenant?.permissions)
            ? (selectedTenant.permissions as Record<
                string,
                TenantPermissionActions
              >)
            : {},
      };

      // マルチテナントJWT生成
      const accessToken = selectedTenant
        ? this.generateMultiTenantJWT(multiTenantUser, selectedTenant)
        : basicLoginResponse.accessToken;

      // テナントコンテキスト保存
      if (selectedTenant) {
        this.saveTenantContext(selectedTenant);
      }
      this.saveAvailableTenants(availableTenants);

      return {
        user: multiTenantUser,
        access_token: accessToken,
        refresh_token: basicLoginResponse.refreshToken,
        expires_in: basicLoginResponse.expiresIn,
        token_type: 'Bearer' as const,
        tenant: selectedTenant
          ? {
              id: selectedTenant.tenantId,
              name: selectedTenant.tenantName,
              slug: selectedTenant.tenantSlug || selectedTenant.subdomain,
              subdomain: selectedTenant.subdomain,
              planType: selectedTenant.planType,
              billingStatus: selectedTenant.billingStatus,
              status: 'active' as const,
              features: selectedTenant.features,
              limits: {
                maxUsers: 100,
                currentUsers: 1,
                maxCustomers: 10000,
                maxReservations: 50000,
                maxFiles: 1000,
                storageLimit: 5000,
                apiRequestsPerMinute: 1000,
                apiRequestsPerHour: 10000,
                apiRequestsPerDay: 100000,
                maxIntegrations: 10,
                maxCustomFields: 50,
                maxReports: 100,
                limitsResetDate: new Date(),
                billingCycleStart: new Date(),
              },
              settings: {
                brandColor: '#000000',
                logoUrl: '',
                customDomain: '',
                enforceSSO: false,
                requireMFA: false,
                sessionTimeout: 480,
                passwordPolicy: {
                  minLength: 8,
                  requireUppercase: true,
                  requireLowercase: true,
                  requireNumbers: true,
                  requireSpecialChars: false,
                  maxAge: 90,
                  preventReuse: 5,
                },
                enabledFeatures: ['basic-booking', 'customer-management'],
                customFields: [],
                allowedIntegrations: [],
                webhookEndpoints: [],
                notificationSettings: {
                  email: {
                    enabled: true,
                    fromAddress: 'noreply@ripia.com',
                    replyToAddress: 'support@ripia.com',
                  },
                  sms: {
                    enabled: false,
                    provider: 'twilio',
                    fromNumber: '+1234567890',
                  },
                  push: {
                    enabled: false,
                  },
                  webhook: {
                    enabled: false,
                  },
                },
                timezone: 'Asia/Tokyo',
                dateFormat: 'YYYY-MM-DD',
                currency: 'JPY',
                language: 'ja-JP',
              },
              createdAt: new Date(),
              updatedAt: new Date(),
            }
          : {
              id: '',
              tenantId: '',
              name: '',
              slug: '',
              subdomain: 'default',
              planType: 'light' as const,
              billingStatus: 'active' as const,
              status: 'active' as const,
              features: [],
              limits: {
                maxUsers: 100,
                currentUsers: 1,
                maxCustomers: 10000,
                maxReservations: 50000,
                maxFiles: 1000,
                storageLimit: 5000,
                apiRequestsPerMinute: 1000,
                apiRequestsPerHour: 10000,
                apiRequestsPerDay: 100000,
                maxIntegrations: 10,
                maxCustomFields: 50,
                maxReports: 100,
                limitsResetDate: new Date(),
                billingCycleStart: new Date(),
              },
              settings: {
                brandColor: '#000000',
                logoUrl: '',
                customDomain: '',
                enforceSSO: false,
                requireMFA: false,
                sessionTimeout: 480,
                passwordPolicy: {
                  minLength: 8,
                  requireUppercase: true,
                  requireLowercase: true,
                  requireNumbers: true,
                  requireSpecialChars: false,
                  maxAge: 90,
                  preventReuse: 5,
                },
                enabledFeatures: ['basic-booking', 'customer-management'],
                customFields: [],
                allowedIntegrations: [],
                webhookEndpoints: [],
                notificationSettings: {
                  email: {
                    enabled: true,
                    fromAddress: 'noreply@ripia.com',
                    replyToAddress: 'support@ripia.com',
                  },
                  sms: {
                    enabled: false,
                    provider: 'twilio',
                    fromNumber: '+1234567890',
                  },
                  push: {
                    enabled: false,
                  },
                  webhook: {
                    enabled: false,
                  },
                },
                timezone: 'Asia/Tokyo',
                dateFormat: 'YYYY-MM-DD',
                currency: 'JPY',
                language: 'ja-JP',
              },
              createdAt: new Date(),
              updatedAt: new Date(),
            },
        session: {
          id: 'session-' + Date.now(),
          deviceId: 'device-' + Date.now(),
          deviceName: 'Web Browser',
          ipAddress: '127.0.0.1',
          location: 'Local',
          createdAt: new Date(),
          lastActivity: new Date(),
          expiresAt: new Date(Date.now() + basicLoginResponse.expiresIn * 1000),
        },
        security: {
          securityLevel: 'standard' as const,
          mfaRequired: false,
          mfaVerified: false,
          passwordChangeRequired: false,
          accountLocked: false,
          suspiciousActivity: false,
        },
        // Backward compatibility for existing tests
        accessToken: accessToken,
        availableTenants: availableTenants,
        selectedTenant: selectedTenant
          ? {
              id: selectedTenant.tenantId,
              tenantId: selectedTenant.tenantId,
              name: selectedTenant.tenantName,
              slug: selectedTenant.tenantSlug || selectedTenant.subdomain,
              subdomain: selectedTenant.subdomain,
              planType: selectedTenant.planType,
              billingStatus: selectedTenant.billingStatus,
              status: 'active' as const,
              features: selectedTenant.features,
              limits: {
                maxUsers: 100,
                currentUsers: 1,
                maxCustomers: 10000,
                maxReservations: 50000,
                maxFiles: 1000,
                storageLimit: 5000,
                apiRequestsPerMinute: 1000,
                apiRequestsPerHour: 10000,
                apiRequestsPerDay: 100000,
                maxIntegrations: 10,
                maxCustomFields: 50,
                maxReports: 100,
                limitsResetDate: new Date(),
                billingCycleStart: new Date(),
              },
              settings: {
                brandColor: '#000000',
                logoUrl: '',
                customDomain: '',
                enforceSSO: false,
                requireMFA: false,
                sessionTimeout: 480,
                passwordPolicy: {
                  minLength: 8,
                  requireUppercase: true,
                  requireLowercase: true,
                  requireNumbers: true,
                  requireSpecialChars: false,
                  maxAge: 90,
                  preventReuse: 5,
                },
                enabledFeatures: ['basic-booking', 'customer-management'],
                customFields: [],
                allowedIntegrations: [],
                webhookEndpoints: [],
                notificationSettings: {
                  email: {
                    enabled: true,
                    fromAddress: 'noreply@ripia.com',
                    replyToAddress: 'support@ripia.com',
                  },
                  sms: {
                    enabled: false,
                    provider: 'twilio',
                    fromNumber: '+1234567890',
                  },
                  push: {
                    enabled: false,
                  },
                  webhook: {
                    enabled: false,
                  },
                },
                timezone: 'Asia/Tokyo',
                dateFormat: 'YYYY-MM-DD',
                currency: 'JPY',
                language: 'ja-JP',
              },
              createdAt: new Date(),
              updatedAt: new Date(),
            }
          : {
              id: '',
              tenantId: '',
              name: '',
              slug: '',
              subdomain: 'default',
              planType: 'light' as const,
              billingStatus: 'active' as const,
              status: 'active' as const,
              features: [],
              limits: {
                maxUsers: 100,
                currentUsers: 1,
                maxCustomers: 10000,
                maxReservations: 50000,
                maxFiles: 1000,
                storageLimit: 5000,
                apiRequestsPerMinute: 1000,
                apiRequestsPerHour: 10000,
                apiRequestsPerDay: 100000,
                maxIntegrations: 10,
                maxCustomFields: 50,
                maxReports: 100,
                limitsResetDate: new Date(),
                billingCycleStart: new Date(),
              },
              settings: {
                brandColor: '#000000',
                logoUrl: '',
                customDomain: '',
                enforceSSO: false,
                requireMFA: false,
                sessionTimeout: 480,
                passwordPolicy: {
                  minLength: 8,
                  requireUppercase: true,
                  requireLowercase: true,
                  requireNumbers: true,
                  requireSpecialChars: false,
                  maxAge: 90,
                  preventReuse: 5,
                },
                enabledFeatures: ['basic-booking', 'customer-management'],
                customFields: [],
                allowedIntegrations: [],
                webhookEndpoints: [],
                notificationSettings: {
                  email: {
                    enabled: true,
                    fromAddress: 'noreply@ripia.com',
                    replyToAddress: 'support@ripia.com',
                  },
                  sms: {
                    enabled: false,
                    provider: 'twilio',
                    fromNumber: '+1234567890',
                  },
                  push: {
                    enabled: false,
                  },
                  webhook: {
                    enabled: false,
                  },
                },
                timezone: 'Asia/Tokyo',
                dateFormat: 'YYYY-MM-DD',
                currency: 'JPY',
                language: 'ja-JP',
              },
              createdAt: new Date(),
              updatedAt: new Date(),
            },
      };
    } catch (error) {
      logger.error('マルチテナントログインエラー:', error);
      throw error;
    }
  }

  /**
   * テナント切り替え
   */
  static async switchTenant(
    request: TenantSwitchRequest
  ): Promise<TenantSwitchResponse> {
    try {
      // 現在のユーザー情報取得
      const currentUser = await this.getCurrentUser();
      if (!currentUser) {
        throw new AuthError('認証が必要です', 'UNAUTHORIZED');
      }

      // 利用可能テナント一覧取得
      const availableTenants = this.loadAvailableTenants();
      const targetTenant = availableTenants.find(
        (t) => t.id === request.tenantId
      );

      if (!targetTenant) {
        throw new AuthError(
          '指定されたテナントにアクセスできません',
          'UNAUTHORIZED'
        );
      }

      // 新しいテナントコンテキスト作成
      const newTenantContext = await this.createTenantContext(
        targetTenant,
        currentUser.id,
        request.storeId
      );

      // MultiTenantUserを更新
      const multiTenantUser: MultiTenantUser = {
        ...currentUser,
        tenantContext: newTenantContext,
        availableTenants,
        tenantRole: newTenantContext.userRole,
        isActive: true,
        permissions:
          typeof newTenantContext.permissions === 'object' &&
          !Array.isArray(newTenantContext.permissions)
            ? (newTenantContext.permissions as Record<
                string,
                TenantPermissionActions
              >)
            : {},
      };

      // 新しいJWT生成
      const accessToken = this.generateMultiTenantJWT(
        multiTenantUser,
        newTenantContext
      );

      // テナントコンテキスト保存
      this.saveTenantContext(newTenantContext);

      return {
        user: multiTenantUser,
        access_token: accessToken,
        tenant: targetTenant,
        permissions: multiTenantUser.permissions,
        // Backward compatibility for existing tests
        accessToken: accessToken,
        expiresIn: 24 * 60 * 60,
      };
    } catch (error) {
      logger.error('テナント切り替えエラー:', error);
      throw error;
    }
  }

  /**
   * 現在のテナントコンテキスト取得
   */
  static getCurrentTenant(): TenantContext | null {
    try {
      return this.loadTenantContext();
    } catch (error) {
      logger.error('テナントコンテキスト取得エラー:', error);
      return null;
    }
  }

  /**
   * 利用可能テナント一覧取得
   */
  static async getTenantList(): Promise<TenantInfo[]> {
    try {
      const currentUser = await this.getCurrentUser();
      if (!currentUser) {
        throw new AuthError('認証が必要です', 'UNAUTHORIZED');
      }

      return this.loadAvailableTenants();
    } catch (error) {
      logger.error('テナント一覧取得エラー:', error);
      return [];
    }
  }

  /**
   * テナント権限チェック
   */
  static hasTenantPermission(permission: string, resource?: string): boolean {
    try {
      const tenantContext = this.getCurrentTenant();
      if (!tenantContext) return false;

      // Owner権限は全アクセス可能
      if (tenantContext.tenantRole === 'owner') return true;

      // Legacy permission check - convert to V2 format
      // For backward compatibility, also support the new permission system
      if (
        resource &&
        typeof tenantContext.permissions === 'object' &&
        !Array.isArray(tenantContext.permissions)
      ) {
        // New format: Record<string, TenantPermissionActions>
        const resourcePermissions = tenantContext.permissions[
          resource
        ] as TenantPermissionActions;
        if (!resourcePermissions) return false;

        // Map permission strings to action booleans
        if (permission === 'read' || permission === 'view')
          return resourcePermissions.read === true;
        if (
          permission === 'write' ||
          permission === 'create' ||
          permission === 'update'
        )
          return resourcePermissions.write === true;
        if (permission === 'delete' || permission === 'remove')
          return resourcePermissions.delete === true;
        if (permission === 'admin' || permission === 'manage')
          return resourcePermissions.admin === true;
        if (permission === 'export') return resourcePermissions.export === true;
        if (permission === 'import') return resourcePermissions.import === true;

        return false;
      } else {
        // Legacy format: string[] - for backward compatibility
        const permissions = Array.isArray(tenantContext.permissions)
          ? (tenantContext.permissions as string[])
          : Object.keys(tenantContext.permissions || {});

        const checkPermission = resource
          ? `${permission}:${resource}`
          : permission;
        return (
          permissions.includes(checkPermission) || permissions.includes('*')
        );
      }
    } catch (error) {
      logger.error('テナント権限チェックエラー:', error);
      return false;
    }
  }

  /**
   * マルチテナント対応JWT生成
   */
  static generateMultiTenantJWT(
    user: MultiTenantUser,
    tenantContext: TenantContext
  ): string {
    const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
    const payload = btoa(
      JSON.stringify({
        // 既存ペイロード（後方互換性）
        sub: user.id,
        email: user.email,
        role: user.role,
        storeId: user.storeId,
        permissions: user.permissions || [],
        // マルチテナント拡張ペイロード
        tenant_id: tenantContext.tenantId,
        tenant_name: tenantContext.tenantName,
        tenant_role: tenantContext.tenantRole,
        tenant_permissions: tenantContext.permissions,
        plan_type: tenantContext.plan,
        plan_features: [], // TODO: プラン機能は後で実装
        current_store_id: tenantContext.currentStoreId,
        available_stores:
          tenantContext.availableStores?.map((store) =>
            typeof store === 'string' ? store : store.code || store.id
          ) || [],
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + 24 * 60 * 60, // 24時間
      })
    );
    const signature = btoa(`multitenant-signature-${Date.now()}`);

    return `${header}.${payload}.${signature}`;
  }

  // ================================================================
  // プライベート・ヘルパーメソッド
  // ================================================================

  /**
   * モックテナント一覧取得（開発用）
   */
  private static async getMockTenantList(
    _userId: string
  ): Promise<TenantInfo[]> {
    // 実際のAPIでは、データベースからユーザーの所属テナント一覧を取得
    return new Promise((resolve) => {
      setTimeout(() => {
        const mockTenants: TenantInfo[] = [
          {
            id: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
            name: '', // 動的に取得（組織名）
            subdomain: '', // 動的に取得（サブドメイン）
            slug: '', // 動的に取得（スラグ）
            planType: 'standard',
            plan: 'standard',
            status: 'active',
            features: ['multi_store', 'advanced_reports', 'customer_analytics'],
            billingStatus: 'active',
            limits: {
              maxUsers: 50,
              currentUsers: 10,
              maxCustomers: 1000,
              maxReservations: 5000,
              maxFiles: 100,
              storageLimit: 1024,
              apiRequestsPerMinute: 100,
              apiRequestsPerHour: 5000,
              apiRequestsPerDay: 50000,
              maxIntegrations: 10,
              maxCustomFields: 50,
              maxReports: 20,
              limitsResetDate: new Date(),
              billingCycleStart: new Date(),
            },
            settings: {
              enforceSSO: false,
              requireMFA: false,
              sessionTimeout: 480,
              passwordPolicy: {
                minLength: 8,
                requireUppercase: true,
                requireLowercase: true,
                requireNumbers: true,
                requireSpecialChars: false,
                maxAge: 90,
                preventReuse: 5,
              },
              enabledFeatures: ['reservations', 'customers', 'reports'],
              customFields: [],
              allowedIntegrations: ['stripe', 'google_calendar'],
              webhookEndpoints: [],
              notificationSettings: {
                email: {
                  enabled: true,
                  fromAddress: undefined,
                  replyToAddress: undefined,
                },
                sms: {
                  enabled: false,
                  provider: undefined,
                  fromNumber: undefined,
                },
                push: {
                  enabled: true,
                },
                webhook: {
                  enabled: false,
                },
              } as NotificationSettings,
              timezone: 'Asia/Tokyo',
              dateFormat: 'YYYY-MM-DD',
              currency: 'JPY',
              language: 'ja',
            },
          },
          {
            id: 'f47ac10b-58cc-4372-a567-0e02b2c3d485',
            name: 'DEMOサロン',
            subdomain: 'demo-salon',
            slug: 'demo-salon',
            planType: 'light',
            plan: 'light',
            status: 'trial',
            features: ['customer_management', 'reservation_management'],
            billingStatus: 'active',
            limits: {
              maxUsers: 10,
              currentUsers: 3,
              maxCustomers: 100,
              maxReservations: 500,
              maxFiles: 20,
              storageLimit: 256,
              apiRequestsPerMinute: 50,
              apiRequestsPerHour: 1000,
              apiRequestsPerDay: 10000,
              maxIntegrations: 3,
              maxCustomFields: 10,
              maxReports: 5,
              limitsResetDate: new Date(),
              billingCycleStart: new Date(),
            },
            settings: {
              enforceSSO: false,
              requireMFA: false,
              sessionTimeout: 240,
              passwordPolicy: {
                minLength: 6,
                requireUppercase: false,
                requireLowercase: true,
                requireNumbers: false,
                requireSpecialChars: false,
                maxAge: 180,
                preventReuse: 3,
              },
              enabledFeatures: ['reservations', 'customers'],
              customFields: [],
              allowedIntegrations: ['stripe'],
              webhookEndpoints: [],
              notificationSettings: {
                email: {
                  enabled: true,
                  fromAddress: undefined,
                  replyToAddress: undefined,
                },
                sms: {
                  enabled: false,
                  provider: undefined,
                  fromNumber: undefined,
                },
                push: {
                  enabled: false,
                },
                webhook: {
                  enabled: false,
                },
              } as NotificationSettings,
              timezone: 'Asia/Tokyo',
              dateFormat: 'YYYY-MM-DD',
              currency: 'JPY',
              language: 'ja',
            },
          },
        ];
        resolve(mockTenants);
      }, 300);
    });
  }

  /**
   * 利用可能店舗一覧を shops API から取得 (Issue #2971)
   *
   * 以前は mock / 空配列固定のため useShopContext.currentStore が常に null になり、
   * サイドバーが fallback "Ripia" を表示する bug の真因だった。
   * 失敗時は空配列で fallback (tenant 表示は維持)。
   */
  private static async fetchAvailableStores(): Promise<TenantStore[]> {
    try {
      const shops = await shopsControllerFindAll();
      return shops.map((shop) => ({
        id: shop.id,
        name: shop.name,
        // ShopResponseDto に code フィールド不在、TenantStore.code は必須のため空文字で埋める
        code: '',
      }));
    } catch (error) {
      logger.error(
        '[Auth] shops API 取得失敗、availableStores 空で fallback',
        error
      );
      return [];
    }
  }

  /**
   * テナントコンテキスト作成
   */
  private static async createTenantContext(
    tenant: TenantInfo,
    _userId: string,
    storeId?: string
  ): Promise<TenantContext> {
    // テナント内での役割決定 (TODO: 将来は API から取得)
    const tenantRole: TenantRole = 'owner';

    const permissions =
      tenantRole === 'owner'
        ? ['*']
        : [
            'customers.read',
            'customers.write',
            'reservations.read',
            'reservations.write',
          ];

    // 利用可能店舗一覧 - shops API から取得 (Issue #2971)
    const availableStores = await this.fetchAvailableStores();

    return {
      // Primary identifiers (既存コード互換性保持)
      id: tenant.id,
      tenantId: tenant.id,
      name: tenant.name,
      tenantName: tenant.name,
      subdomain: tenant.subdomain,
      tenantSlug: tenant.slug,

      // Plan and features
      planType: tenant.planType,
      plan: tenant.plan,
      features: tenant.features,
      billingStatus: tenant.billingStatus,

      // User role in tenant (既存コード互換性保持)
      userRole: tenantRole,
      tenantRole,

      // Permissions
      permissions,

      // Store context (既存コード互換)
      currentStoreId: storeId || availableStores[0]?.id,
      availableStores,
    };
  }

  /**
   * テナントコンテキスト保存
   */
  private static saveTenantContext(tenantContext: TenantContext): void {
    try {
      saveEncryptedToken(
        JSON.stringify(tenantContext),
        TOKEN_KEYS.TENANT_CONTEXT
      );
    } catch (error) {
      logger.error('テナントコンテキスト保存エラー:', error);
      throw new AuthError('テナント情報の保存に失敗しました', 'SERVER_ERROR');
    }
  }

  private static normalizeTenantStoreContext(
    tenantContext: TenantContext
  ): TenantContext {
    if (
      !tenantContext.currentStoreId ||
      (tenantContext.availableStores && tenantContext.availableStores.length > 0)
    ) {
      return tenantContext;
    }

    return {
      ...tenantContext,
      availableStores: [
        {
          id: tenantContext.currentStoreId,
          name: tenantContext.tenantName || tenantContext.name || '店舗',
          code: '',
        },
      ],
    };
  }

  /**
   * テナントコンテキスト読み込み
   */
  private static loadTenantContext(): TenantContext | null {
    try {
      migrateLegacyTenantContextStorage();
      const contextData = loadEncryptedToken(TOKEN_KEYS.TENANT_CONTEXT);
      const tenantContext = contextData
        ? (JSON.parse(contextData) as TenantContext)
        : null;
      return tenantContext
        ? this.normalizeTenantStoreContext(tenantContext)
        : null;
    } catch (error) {
      logger.error('テナントコンテキスト読み込みエラー:', error);
      return null;
    }
  }

  /**
   * 利用可能テナント一覧保存
   */
  private static saveAvailableTenants(tenants: TenantInfo[]): void {
    try {
      saveEncryptedToken(JSON.stringify(tenants), TOKEN_KEYS.AVAILABLE_TENANTS);
    } catch (error) {
      logger.error('テナント一覧保存エラー:', error);
      throw new AuthError('テナント一覧の保存に失敗しました', 'SERVER_ERROR');
    }
  }

  /**
   * 利用可能テナント一覧読み込み
   */
  private static loadAvailableTenants(): TenantInfo[] {
    try {
      const tenantsData = loadEncryptedToken(TOKEN_KEYS.AVAILABLE_TENANTS);
      return tenantsData ? JSON.parse(tenantsData) : [];
    } catch (error) {
      logger.error('テナント一覧読み込みエラー:', error);
      return [];
    }
  }

  /**
   * マルチテナント関連トークンクリア（ログアウト時）
   */
  static clearMultiTenantTokens(): void {
    try {
      removeEncryptedToken(TOKEN_KEYS.TENANT_CONTEXT);
      removeEncryptedToken(TOKEN_KEYS.AVAILABLE_TENANTS);
      removeEncryptedToken(TOKEN_KEYS.SELECTED_TENANT);
    } catch (error) {
      logger.error('マルチテナントトークンクリアエラー:', error);
    }
  }

  /**
   * 全トークンクリア（エンタープライズセキュリティ統合）
   */
  static clearAllTokens(): void {
    try {
      // 既存トークンクリア
      this.clearTokens();
      this.clearMultiTenantTokens();

      // エンタープライズセキュリティトークンクリア
      removeEncryptedToken('ripia_jwt_jti');
      removeEncryptedToken('ripia_device_fingerprint');
      removeEncryptedToken('ripia_security_level');
      removeEncryptedToken('ripia_session_id');
      removeEncryptedToken('ripia_device_trust_score');
      removeEncryptedToken('ripia_last_activity');
    } catch (error) {
      logger.error('全トークンクリアエラー:', error);
    }
  }

  // ================================================================
  // エンタープライズセキュリティ管理機能
  // ================================================================

  /**
   * アクティブセッション一覧取得
   */
  static async getActiveSessions(): Promise<SecurityAuditInfo[]> {
    try {
      const currentUser = await this.getCurrentUser();
      if (!currentUser) {
        throw new AuthError('認証が必要です', 'UNAUTHORIZED');
      }

      // TODO: 実装時はAPIからセッション一覧を取得
      // モック実装
      return [
        {
          tokenId: loadEncryptedToken('ripia_jwt_jti') || 'current-session',
          issuedAt: Date.now() - 2 * 60 * 60 * 1000, // 2時間前
          expiresAt: Date.now() + 22 * 60 * 60 * 1000, // 22時間後
          deviceFingerprint:
            loadEncryptedToken('ripia_device_fingerprint') || undefined,
          lastActivity: Date.now() - 5 * 60 * 1000, // 5分前
          securityLevel:
            loadEncryptedToken('ripia_security_level') || 'standard',
        },
      ];
    } catch (error) {
      logger.error('アクティブセッション取得エラー:', error);
      return [];
    }
  }

  /**
   * 特定セッション無効化
   */
  static async revokeSession(tokenId: string): Promise<boolean> {
    try {
      const currentUser = await this.getCurrentUser();
      if (!currentUser) {
        throw new AuthError('認証が必要です', 'UNAUTHORIZED');
      }

      const revocationRequest: RevocationRequest = {
        jti: tokenId,
        userId: currentUser.id,
        reason: 'admin',
      };

      return await TokenBlacklistService.revokeToken(revocationRequest);
    } catch (error) {
      logger.error('セッション無効化エラー:', error);
      return false;
    }
  }

  /**
   * 全セッション無効化（現在のセッション以外）
   */
  static async revokeAllOtherSessions(): Promise<number> {
    try {
      const currentUser = await this.getCurrentUser();
      if (!currentUser) {
        throw new AuthError('認証が必要です', 'UNAUTHORIZED');
      }

      const currentJti = loadEncryptedToken('ripia_jwt_jti');
      const revokedCount = await TokenBlacklistService.revokeAllUserTokens(
        currentUser.id,
        'security'
      );

      // 現在のセッションのJTIをホワイトリストに再追加（モック実装）
      if (currentJti) {
        // TODO: 実装時は適切なホワイトリスト管理を実装
        logger.info(`現在のセッション ${currentJti} は保持されました`);
      }

      return revokedCount;
    } catch (error) {
      logger.error('他セッション無効化エラー:', error);
      return 0;
    }
  }

  /**
   * デバイス管理情報取得
   */
  static async getDeviceInfo(): Promise<DeviceManagementInfo | null> {
    try {
      const deviceFingerprint = loadEncryptedToken('ripia_device_fingerprint');
      if (!deviceFingerprint) return null;

      const trustScore = parseInt(
        loadEncryptedToken('ripia_device_trust_score') || '50',
        10
      );
      const lastUsed = parseInt(
        loadEncryptedToken('ripia_last_activity') || Date.now().toString(),
        10
      );

      return {
        fingerprint: deviceFingerprint,
        trustScore,
        lastUsed,
        isBlocked: false, // TODO: ブラックリスト管理実装
        deviceName: 'Current Device', // TODO: デバイス名管理実装
      };
    } catch (error) {
      logger.error('デバイス情報取得エラー:', error);
      return null;
    }
  }

  // ====================================
  // Multi-Tenant v2 API Integration
  // backend/Issue #282統合対応
  // ====================================

  /**
   * Multi-Tenant v2 API Call
   * backend/のMultiTenantAuthGuardと完全統合
   */
  static async callV2API<T>(
    endpoint: string,
    config: ApiRequestConfig = {}
  ): Promise<T> {
    try {
      const url = `${API_CONFIG.BASE_URL}${endpoint}`;
      const headers = await this.setupMultiTenantHeaders(config.headers);

      const requestConfig: RequestInit = {
        method: config.method || 'GET',
        headers,
      };

      // Add body only if it exists to avoid spread type issues
      if (config.body) {
        requestConfig.body =
          typeof config.body === 'string'
            ? config.body
            : JSON.stringify(config.body);
      }

      logger.log(`[V2 API] ${config.method || 'GET'} ${endpoint}`, {
        headers: this.sanitizeHeaders(headers),
      });

      const response = await ApiClient.fetchWithTimeout(
        url,
        requestConfig,
        config.timeout
      );

      if (!response.ok) {
        await this.handleV2ResponseError(response);
      }

      const rawData = await ApiClient.handleResponse<unknown>(response);
      return unwrapResponse<T>(rawData, `callV2API:${endpoint}`);
    } catch (error) {
      logger.error('[V2 API] Request failed:', error);
      throw this.transformToMultiTenantError(error);
    }
  }

  /**
   * Multi-Tenant Headers Setup
   * backend/のMultiTenantAuthGuardに必要なヘッダーを自動付与
   */
  private static async setupMultiTenantHeaders(
    customHeaders: Record<string, string> = {}
  ): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      [SENT_CUSTOM_HEADERS[0]]: 'application/json',
      ...customHeaders,
    };

    // JWT Token付与 (Issue #1618: Clerk SDK優先、localStorageフォールバック)
    let token: string | null = null;

    // 1. Clerk SDKからトークン取得を試行 (apiClient.tsと同じパターン)
    try {
      const clerk = (
        window as unknown as {
          Clerk?: { session?: { getToken: () => Promise<string | null> } };
        }
      ).Clerk;
      if (clerk?.session) {
        token = await clerk.session.getToken();
      }
    } catch (error) {
      logger.warn(
        '[AuthService] Clerk token retrieval failed, falling back to localStorage:',
        error
      );
    }

    // 2. フォールバック: localStorageトークン（レガシー互換）
    if (!token) {
      token = loadEncryptedToken(TOKEN_KEYS.ACCESS_TOKEN);
    }

    if (token) {
      headers[SENT_CUSTOM_HEADERS[1]] = `Bearer ${token}`;
    }

    // Tenant Context付与
    const tenantContext = this.loadTenantContext();
    if (tenantContext) {
      headers[SENT_CUSTOM_HEADERS[2]] = tenantContext.tenantId;
      headers[SENT_CUSTOM_HEADERS[3]] =
        tenantContext.tenantSlug || tenantContext.subdomain;

      // Optional: Tenant Context as JSON (for debugging)
      if (import.meta.env?.MODE === 'development') {
        headers[SENT_CUSTOM_HEADERS[4]] = JSON.stringify({
          tenantId: tenantContext.tenantId,
          tenantName: tenantContext.tenantName,
          tenantRole: tenantContext.tenantRole,
          plan: tenantContext.plan,
        });
      }
    }

    // Request Tracking
    headers[SENT_CUSTOM_HEADERS[5]] = this.generateRequestId();
    headers[SENT_CUSTOM_HEADERS[6]] = this.getClientVersion();
    headers[SENT_CUSTOM_HEADERS[7]] = navigator.userAgent;

    return headers;
  }

  /**
   * V2 API Error Handler
   * backend/のMultiTenantExceptionFilterエラー形式対応
   */
  private static async handleV2ResponseError(
    response: Response
  ): Promise<never> {
    let errorData: MultiTenantApiError;

    try {
      errorData = await response.json();
    } catch {
      // JSON parse失敗時のフォールバック
      errorData = {
        statusCode: response.status,
        error: response.statusText,
        message: 'Unknown API error',
        timestamp: new Date().toISOString(),
        path: response.url,
      };
    }

    // Security Error Classification
    const securityType = this.classifySecurityError(errorData);
    if (securityType) {
      errorData.details = {
        ...errorData.details,
        securityErrorType: securityType,
        suggestions: this.getErrorSuggestions(securityType),
      };
    }

    // Log security events
    if (securityType) {
      logger.warn('[Security Event]', {
        type: securityType,
        statusCode: errorData.statusCode,
        tenantId: errorData.tenantId,
        message: errorData.message,
        timestamp: errorData.timestamp,
      });
    }

    // Automatic token refresh for 401 errors
    if (
      response.status === 401 &&
      securityType === SecurityErrorType.SESSION_EXPIRED
    ) {
      try {
        await this.refreshToken();
        throw new AuthError(
          'Token refreshed, please retry the request',
          'TOKEN_REFRESHED'
        );
      } catch (refreshError) {
        logger.error('[V2 API] Token refresh failed:', refreshError);
        // Force logout if refresh fails
        await this.logout();
        throw new AuthError(
          'Session expired, please login again',
          'SESSION_EXPIRED'
        );
      }
    }

    throw this.createMultiTenantError(errorData);
  }

  /**
   * Security Error Classification
   * backend/のSecurityErrorTypeに基づく分類
   */
  private static classifySecurityError(
    errorData: MultiTenantApiError
  ): SecurityErrorType | null {
    const message =
      typeof errorData.message === 'string'
        ? errorData.message
        : errorData.message[0] || '';
    const statusCode = errorData.statusCode;

    switch (statusCode) {
      case 401:
        if (message.toLowerCase().includes('expired')) {
          return SecurityErrorType.SESSION_EXPIRED;
        }
        if (message.toLowerCase().includes('token')) {
          return SecurityErrorType.INVALID_TOKEN;
        }
        return SecurityErrorType.AUTHENTICATION_FAILED;

      case 403:
        if (message.toLowerCase().includes('tenant')) {
          return SecurityErrorType.TENANT_ACCESS_VIOLATION;
        }
        if (message.toLowerCase().includes('permission')) {
          return SecurityErrorType.PERMISSION_DENIED;
        }
        if (
          message.toLowerCase().includes('plan') ||
          message.toLowerCase().includes('feature')
        ) {
          return SecurityErrorType.PLAN_RESTRICTION;
        }
        return SecurityErrorType.AUTHORIZATION_FAILED;

      case 429:
        return SecurityErrorType.RATE_LIMIT_EXCEEDED;

      default:
        return null;
    }
  }

  /**
   * Error Suggestions
   * backend/のMultiTenantExceptionFilterと同じ提案
   */
  private static getErrorSuggestions(
    securityType: SecurityErrorType
  ): string[] {
    const suggestions: Record<SecurityErrorType, string[]> = {
      [SecurityErrorType.AUTHENTICATION_FAILED]: [
        'Please check your credentials',
        'Ensure you are logged in',
      ],
      [SecurityErrorType.INVALID_TOKEN]: [
        'Please log in again',
        'Your session may have been corrupted',
      ],
      [SecurityErrorType.SESSION_EXPIRED]: [
        'Please log in again',
        'Your session has expired',
      ],
      [SecurityErrorType.PERMISSION_DENIED]: [
        'Contact your administrator for access',
        'You may need additional permissions',
      ],
      [SecurityErrorType.PLAN_RESTRICTION]: [
        'Consider upgrading your plan',
        'This feature is not available in your current plan',
      ],
      [SecurityErrorType.RATE_LIMIT_EXCEEDED]: [
        'Please slow down your requests',
        'Try again in a few minutes',
      ],
      [SecurityErrorType.TENANT_ACCESS_VIOLATION]: [
        "You can only access your own organization's data",
        'Please contact support if this is unexpected',
      ],
      [SecurityErrorType.AUTHORIZATION_FAILED]: [
        'You do not have permission to perform this action',
      ],
      [SecurityErrorType.FEATURE_UNAVAILABLE]: [
        'This feature is not available',
        'Contact support for more information',
      ],
      [SecurityErrorType.SUSPICIOUS_ACTIVITY]: [
        'Your account may be compromised',
        'Please contact support immediately',
      ],
    };

    return suggestions[securityType] || [];
  }

  /**
   * Create Multi-Tenant Error
   */
  private static createMultiTenantError(
    errorData: MultiTenantApiError
  ): AuthError {
    const authError = new AuthError(
      typeof errorData.message === 'string'
        ? errorData.message
        : errorData.message.join(', '),
      this.mapStatusCodeToAuthErrorType(errorData.statusCode)
    );

    // Attach additional multi-tenant error data
    const multiTenantData: MultiTenantData = {
      tenantId: errorData.tenantId,
      additionalInfo: {
        statusCode: errorData.statusCode,
        error: errorData.error,
        message: errorData.message,
        timestamp: errorData.timestamp,
        path: errorData.path,
        requestId: errorData.requestId,
        correlationId: errorData.correlationId,
        details: errorData.details,
      },
    };
    (
      authError as AuthError & { multiTenantData?: MultiTenantData }
    ).multiTenantData = multiTenantData;

    return authError;
  }

  /**
   * Transform any error to Multi-Tenant Error
   */
  private static transformToMultiTenantError(error: unknown): AuthError {
    if (error instanceof AuthError) {
      return error;
    }

    // Type guard for Error-like objects
    if (error instanceof Error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        return new AuthError(
          'Network error. Please check your connection.',
          'NETWORK_ERROR'
        );
      }
      return new AuthError(
        error.message || 'Unknown error occurred',
        'SERVER_ERROR'
      );
    }

    // Handle non-Error objects
    const errorMessage =
      typeof error === 'string' ? error : 'Unknown error occurred';
    return new AuthError(errorMessage, 'SERVER_ERROR');
  }

  /**
   * Map HTTP status codes to AuthError types
   */
  private static mapStatusCodeToAuthErrorType(
    statusCode: number
  ): AuthError['code'] {
    const mapping: Record<number, AuthError['code']> = {
      400: 'VALIDATION_ERROR',
      401: 'AUTHENTICATION_FAILED',
      403: 'AUTHORIZATION_FAILED',
      404: 'NOT_FOUND',
      409: 'CONFLICT',
      422: 'VALIDATION_ERROR',
      429: 'RATE_LIMIT_EXCEEDED',
      500: 'SERVER_ERROR',
      502: 'SERVER_ERROR',
      503: 'SERVICE_UNAVAILABLE',
    };

    return mapping[statusCode] || 'SERVER_ERROR';
  }

  /**
   * V2 API Convenience Methods
   */
  static async getV2<T>(
    endpoint: string,
    config: Omit<ApiRequestConfig, 'method'> = {}
  ): Promise<T> {
    return this.callV2API<T>(endpoint, { ...config, method: 'GET' });
  }

  static async postV2<T>(
    endpoint: string,
    data: unknown,
    config: Omit<ApiRequestConfig, 'method' | 'body'> = {}
  ): Promise<T> {
    return this.callV2API<T>(endpoint, {
      ...config,
      method: 'POST',
      body: data,
    });
  }

  static async putV2<T>(
    endpoint: string,
    data: unknown,
    config: Omit<ApiRequestConfig, 'method' | 'body'> = {}
  ): Promise<T> {
    return this.callV2API<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: data,
    });
  }

  static async patchV2<T>(
    endpoint: string,
    data: unknown,
    config: Omit<ApiRequestConfig, 'method' | 'body'> = {}
  ): Promise<T> {
    return this.callV2API<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: data,
    });
  }

  static async deleteV2<T>(
    endpoint: string,
    config: Omit<ApiRequestConfig, 'method'> = {}
  ): Promise<T> {
    return this.callV2API<T>(endpoint, { ...config, method: 'DELETE' });
  }

  /**
   * Multi-Tenant Authentication with v2 API
   */
  static async loginV2(
    credentials: MultiTenantLoginRequest
  ): Promise<MultiTenantLoginResponse> {
    try {
      logger.log('[Auth V2] Login attempt', {
        email: credentials.email,
        tenantSubdomain: credentials.tenantSubdomain,
      });

      const response = await this.callV2API<MultiTenantLoginResponse>(
        '/v2/auth/login',
        {
          method: 'POST',
          body: credentials,
        }
      );

      // Save enhanced tokens and context
      saveEncryptedToken(TOKEN_KEYS.ACCESS_TOKEN, response.access_token);
      saveEncryptedToken(TOKEN_KEYS.REFRESH_TOKEN, response.refresh_token);

      // Save tenant context
      if (response.tenant) {
        // Issue #2971: shops API から実店舗取得 (従来は空配列 TODO)
        const availableStores = await this.fetchAvailableStores();
        this.saveTenantContext({
          // Primary identifiers (既存コード互換性保持)
          id: response.tenant.id,
          tenantId: response.tenant.id,
          name: response.tenant.name,
          tenantName: response.tenant.name,
          subdomain: response.tenant.subdomain,
          tenantSlug: response.tenant.subdomain,

          // Plan and features
          planType: response.tenant.planType,
          plan: response.tenant.planType,
          features: response.tenant.features || [],
          billingStatus: response.tenant.billingStatus || 'active',

          // User role in tenant (既存コード互換性保持)
          userRole: response.user.tenantRole,
          tenantRole: response.user.tenantRole,

          // Permissions
          permissions: Object.keys(response.user.permissions),

          // Store context
          currentStoreId: availableStores[0]?.id,
          availableStores,
        });
      }

      // Save user data
      saveEncryptedToken(JSON.stringify(response.user), TOKEN_KEYS.USER_DATA);

      logger.log('[Auth V2] Login successful', {
        userId: response.user.id,
        tenantId: response.tenant.id,
        plan: response.tenant.planType,
      });

      return response;
    } catch (error) {
      logger.error('[Auth V2] Login failed:', error);
      throw error;
    }
  }

  /**
   * Enhanced Tenant Switch with v2 API
   */
  static async switchTenantV2(
    request: TenantSwitchRequest
  ): Promise<TenantSwitchResponse> {
    try {
      logger.log('[Auth V2] Tenant switch attempt', {
        tenantId: request.tenantId,
      });

      const response = await this.callV2API<TenantSwitchResponse>(
        '/v2/auth/switch-tenant',
        {
          method: 'POST',
          body: request,
        }
      );

      // Update tokens and context
      saveEncryptedToken(TOKEN_KEYS.ACCESS_TOKEN, response.access_token);

      // Issue #2971: shops API から実店舗取得 (従来は空配列 TODO)
      const availableStores = await this.fetchAvailableStores();

      // Update tenant context
      this.saveTenantContext({
        // Primary identifiers (既存コード互換性保持)
        id: response.tenant.id,
        tenantId: response.tenant.id,
        name: response.tenant.name,
        tenantName: response.tenant.name,
        subdomain: response.tenant.subdomain,
        tenantSlug: response.tenant.subdomain,

        // Plan and features
        planType: response.tenant.planType,
        plan: response.tenant.planType,
        features: response.tenant.features || [],
        billingStatus: response.tenant.billingStatus || 'active',

        // User role in tenant (既存コード互換性保持)
        userRole: response.user.tenantRole,
        tenantRole: response.user.tenantRole,

        // Permissions
        permissions: Object.keys(response.permissions),

        // Store context
        currentStoreId: availableStores[0]?.id,
        availableStores,
      });

      // Update user data
      saveEncryptedToken(JSON.stringify(response.user), TOKEN_KEYS.USER_DATA);

      logger.log('[Auth V2] Tenant switch successful', {
        tenantId: response.tenant.id,
        userId: response.user.id,
      });

      return response;
    } catch (error) {
      logger.error('[Auth V2] Tenant switch failed:', error);
      throw error;
    }
  }

  // ====================================
  // Utility Methods
  // ====================================

  /**
   * Generate Request ID
   */
  private static generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get Client Version
   */
  private static getClientVersion(): string {
    return import.meta.env?.VITE_APP_VERSION || '1.0.0';
  }

  /**
   * Sanitize Headers for Logging
   */
  private static sanitizeHeaders(
    headers: Record<string, string>
  ): Record<string, string> {
    const sanitized = { ...headers };
    const sensitiveHeaders = ['authorization', 'x-api-key', 'x-secret'];

    for (const header of sensitiveHeaders) {
      if (sanitized[header]) {
        sanitized[header] = '[REDACTED]';
      }
    }

    return sanitized;
  }

  // ====================================
  // V2 Endpoint URL Builders
  // ====================================

  /**
   * Build V2 API URL with parameter substitution
   * @param endpoint - Endpoint template with {param} placeholders
   * @param params - Parameters to substitute
   * @returns Built URL
   */
  static buildV2Url(
    endpoint: string,
    params: Record<string, string> = {}
  ): string {
    let url = endpoint;

    // Replace {param} placeholders with actual values
    for (const [key, value] of Object.entries(params)) {
      url = url.replace(`{${key}}`, encodeURIComponent(value));
    }

    return url;
  }

  /**
   * V2 Users API URL Builders
   */
  static buildUsersUrl = {
    list: () => API_CONFIG.ENDPOINTS.V2_USERS.LIST,
    create: () => API_CONFIG.ENDPOINTS.V2_USERS.CREATE,
    getById: (id: string) =>
      this.buildV2Url(API_CONFIG.ENDPOINTS.V2_USERS.GET_BY_ID, { id }),
    update: (id: string) =>
      this.buildV2Url(API_CONFIG.ENDPOINTS.V2_USERS.UPDATE, { id }),
    delete: (id: string) =>
      this.buildV2Url(API_CONFIG.ENDPOINTS.V2_USERS.DELETE, { id }),
    getPermissions: (id: string) =>
      this.buildV2Url(API_CONFIG.ENDPOINTS.V2_USERS.GET_PERMISSIONS, { id }),
    bulkAction: () => API_CONFIG.ENDPOINTS.V2_USERS.BULK_ACTION,
    debugContext: () => API_CONFIG.ENDPOINTS.V2_USERS.DEBUG_CONTEXT,
  };

  /**
   * V2 Auth API URL Builders
   */
  static buildAuthUrl = {
    login: () => API_CONFIG.ENDPOINTS.V2_AUTH.LOGIN,
    refresh: () => API_CONFIG.ENDPOINTS.V2_AUTH.REFRESH,
    logout: () => API_CONFIG.ENDPOINTS.V2_AUTH.LOGOUT,
    me: () => API_CONFIG.ENDPOINTS.V2_AUTH.ME,
    switchTenant: () => API_CONFIG.ENDPOINTS.V2_AUTH.SWITCH_TENANT,
    verifyToken: () => API_CONFIG.ENDPOINTS.V2_AUTH.VERIFY_TOKEN,
    revokeToken: () => API_CONFIG.ENDPOINTS.V2_AUTH.REVOKE_TOKEN,
    mfaSetup: () => API_CONFIG.ENDPOINTS.V2_AUTH.MFA_SETUP,
    mfaVerify: () => API_CONFIG.ENDPOINTS.V2_AUTH.MFA_VERIFY,
    passwordPolicy: () => API_CONFIG.ENDPOINTS.V2_AUTH.PASSWORD_POLICY,
    securityStatus: () => API_CONFIG.ENDPOINTS.V2_AUTH.SECURITY_STATUS,
  };

  /**
   * V2 Tenants API URL Builders
   */
  static buildTenantsUrl = {
    list: () => API_CONFIG.ENDPOINTS.V2_TENANTS.LIST,
    getCurrent: () => API_CONFIG.ENDPOINTS.V2_TENANTS.GET_CURRENT,
    getById: (id: string) =>
      this.buildV2Url(API_CONFIG.ENDPOINTS.V2_TENANTS.GET_BY_ID, { id }),
    update: (id: string) =>
      this.buildV2Url(API_CONFIG.ENDPOINTS.V2_TENANTS.UPDATE, { id }),
    getSettings: (id: string) =>
      this.buildV2Url(API_CONFIG.ENDPOINTS.V2_TENANTS.GET_SETTINGS, { id }),
    updateSettings: (id: string) =>
      this.buildV2Url(API_CONFIG.ENDPOINTS.V2_TENANTS.UPDATE_SETTINGS, { id }),
    getAnalytics: (id: string) =>
      this.buildV2Url(API_CONFIG.ENDPOINTS.V2_TENANTS.GET_ANALYTICS, { id }),
    getRateLimits: (id: string) =>
      this.buildV2Url(API_CONFIG.ENDPOINTS.V2_TENANTS.GET_RATE_LIMITS, { id }),
  };

  /**
   * V2 API High-level Methods (Issue #283 Requirements)
   */

  // Users Management
  static async getUsers(params?: {
    page?: number;
    limit?: number;
    role?: string;
    search?: string;
  }) {
    const url = this.buildUsersUrl.list();
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.role) queryParams.append('role', params.role);
    if (params?.search) queryParams.append('search', params.search);

    const fullUrl = queryParams.toString() ? `${url}?${queryParams}` : url;
    return this.getV2(fullUrl);
  }

  static async createUser(userData: unknown) {
    return this.postV2(this.buildUsersUrl.create(), userData);
  }

  static async getUserById(id: string) {
    return this.getV2(this.buildUsersUrl.getById(id));
  }

  static async updateUser(id: string, userData: unknown) {
    return this.patchV2(this.buildUsersUrl.update(id), userData);
  }

  static async deleteUser(id: string) {
    return this.deleteV2(this.buildUsersUrl.delete(id));
  }

  static async getUserPermissions(id: string) {
    return this.getV2(this.buildUsersUrl.getPermissions(id));
  }

  // Tenant Management
  static async getCurrentTenantV2() {
    return this.getV2(this.buildTenantsUrl.getCurrent());
  }

  static async getTenantById(id: string) {
    return this.getV2(this.buildTenantsUrl.getById(id));
  }

  static async updateTenant(id: string, tenantData: unknown) {
    return this.patchV2(this.buildTenantsUrl.update(id), tenantData);
  }

  static async getTenantSettings(id: string) {
    return this.getV2(this.buildTenantsUrl.getSettings(id));
  }

  static async updateTenantSettings(id: string, settings: unknown) {
    return this.patchV2(this.buildTenantsUrl.updateSettings(id), settings);
  }

  static async getTenantAnalytics(id: string) {
    return this.getV2(this.buildTenantsUrl.getAnalytics(id));
  }

  static async getTenantRateLimits(id: string) {
    return this.getV2(this.buildTenantsUrl.getRateLimits(id));
  }

  // Security & Auth (Type-Safe V2 Methods)
  static async getSecurityStatus(): Promise<{
    securityLevel: 'standard' | 'high' | 'premium';
    mfaRequired: boolean;
    passwordChangeRequired: boolean;
    accountLocked: boolean;
    suspiciousActivity: boolean;
    lastLogin?: string;
    lastPasswordChange?: string;
  }> {
    return this.getV2(this.buildAuthUrl.securityStatus());
  }

  static async getPasswordPolicy(): Promise<{
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumbers: boolean;
    requireSpecialChars: boolean;
    maxAge: number;
    preventReuse: number;
  }> {
    return this.getV2(this.buildAuthUrl.passwordPolicy());
  }

  static async verifyToken(): Promise<{
    valid: boolean;
    expiresIn: number;
    user: UserInfo;
    tenant: TenantInfo;
  }> {
    return this.postV2(this.buildAuthUrl.verifyToken(), {});
  }

  static async revokeToken(
    tokenId?: string
  ): Promise<{ revoked: boolean; message: string }> {
    return this.postV2(this.buildAuthUrl.revokeToken(), { tokenId });
  }

  // ====================================
  // Enhanced Type-Safe Methods
  // ====================================

  /**
   * Get current user with enhanced typing
   */
  static getCurrentUserV2(): UserInfo | null {
    try {
      const userData = loadEncryptedToken(TOKEN_KEYS.USER_DATA);
      if (!userData) return null;

      const user = JSON.parse(userData);
      // Convert legacy user to UserInfo format
      // Convert string[] permissions to Record<string, TenantPermissionActions> format
      const convertPermissions = (
        legacyPermissions?: string[]
      ): Record<string, TenantPermissionActions> => {
        if (!Array.isArray(legacyPermissions)) return {};

        const permissionMap: Record<string, TenantPermissionActions> = {};
        legacyPermissions.forEach((permission) => {
          const [resource, action] = permission.split(':');
          if (!resource) return;

          if (!permissionMap[resource]) {
            permissionMap[resource] = {};
          }

          // Map legacy permission strings to action booleans
          if (action) {
            if (action === 'read' || action === 'view')
              permissionMap[resource].read = true;
            else if (
              action === 'write' ||
              action === 'create' ||
              action === 'update'
            )
              permissionMap[resource].write = true;
            else if (action === 'delete' || action === 'remove')
              permissionMap[resource].delete = true;
            else if (action === 'admin' || action === 'manage')
              permissionMap[resource].admin = true;
            else if (action === 'export') permissionMap[resource].export = true;
            else if (action === 'import') permissionMap[resource].import = true;
          } else {
            // If no action specified, grant all permissions for backward compatibility
            permissionMap[resource] = {
              read: true,
              write: true,
              delete: false,
              admin: false,
            };
          }
        });

        return permissionMap;
      };

      return {
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
        tenantRole: user.tenantRole || 'staff',
        permissions: convertPermissions(user.permissions),
        isActive: user.isActive !== undefined ? user.isActive : true,
        lastLogin: user.lastLoginAt || user.lastLogin,
      };
    } catch (error) {
      logger.error('Failed to get current user:', error);
      return null;
    }
  }

  /**
   * Get current tenant context with enhanced typing
   */
  static getCurrentTenantContextV2(): TenantContext | null {
    try {
      migrateLegacyTenantContextStorage();
      const tenantData = loadEncryptedToken(TOKEN_KEYS.TENANT_CONTEXT);
      if (!tenantData) return null;

      return this.normalizeTenantStoreContext(
        JSON.parse(tenantData) as TenantContext
      );
    } catch (error) {
      logger.error('Failed to get tenant context:', error);
      return null;
    }
  }

  /**
   * Enhanced permission check with tenant context
   */
  static hasV2Permission(
    resource: TenantResource,
    action: keyof TenantPermissionActions
  ): boolean {
    try {
      const tenantContext = this.getCurrentTenantContextV2();
      if (!tenantContext) return false;

      // Handle both legacy string[] and new Record format
      if (Array.isArray(tenantContext.permissions)) {
        // Legacy format: string[]
        const legacyPermission = `${action}:${resource}`;
        return (
          tenantContext.permissions.includes(legacyPermission) ||
          tenantContext.permissions.includes('*') ||
          tenantContext.permissions.includes(resource)
        );
      } else {
        // New format: Record<string, TenantPermissionActions>
        const permissions = tenantContext.permissions[resource];
        if (!permissions) return false;

        return permissions[action] === true;
      }
    } catch (error) {
      logger.error('Permission check failed:', error);
      return false;
    }
  }

  /**
   * Check if tenant has specific feature
   */
  static hasTenantFeature(feature: PlanFeature): boolean {
    try {
      const tenantContext = this.getCurrentTenantContextV2();
      if (!tenantContext) return false;

      return tenantContext.features.includes(feature);
    } catch (error) {
      logger.error('Feature check failed:', error);
      return false;
    }
  }

  /**
   * Enhanced error handling with MultiTenantApiError
   */
  static handleV2ApiError(error: unknown): MultiTenantApiError {
    // Type guard for Axios error with response
    if (
      typeof error === 'object' &&
      error !== null &&
      'response' in error &&
      typeof (error as { response?: unknown }).response === 'object' &&
      (error as { response?: unknown }).response !== null &&
      'data' in (error as { response: { data: unknown } }).response
    ) {
      const axiosError = error as {
        response: {
          data: {
            message?: string;
            error?: string;
            statusCode?: number;
            timestamp?: string;
            path?: string;
            requestId?: string;
            tenantId?: string;
            correlationId?: string;
            details?: unknown;
          };
          status: number;
        };
      };
      const errorData = axiosError.response.data;

      return {
        statusCode: errorData?.statusCode || axiosError.response.status,
        error: errorData?.error || 'Unknown Error',
        message: errorData?.message || 'An error occurred',
        timestamp: errorData?.timestamp || new Date().toISOString(),
        path: errorData?.path || '',
        requestId: errorData?.requestId,
        tenantId: errorData?.tenantId,
        correlationId: errorData?.correlationId,
        details: errorData?.details as { [key: string]: unknown } | undefined,
      };
    }

    // Handle standard Error objects
    if (error instanceof Error) {
      return {
        statusCode: 500,
        error: 'Internal Server Error',
        message: error.message,
        timestamp: new Date().toISOString(),
        path: '',
      };
    }

    // Handle other types of errors
    const errorMessage =
      typeof error === 'string' ? error : 'Unknown error occurred';
    return {
      statusCode: 500,
      error: 'Internal Server Error',
      message: errorMessage,
      timestamp: new Date().toISOString(),
      path: '',
    };
  }

  /**
   * Save enhanced V2 login response
   */
  static saveV2LoginResponse(response: MultiTenantLoginResponse): void {
    const existingTenantContext = this.getCurrentTenantContextV2();
    const canReuseExistingStoreContext =
      existingTenantContext?.tenantId === response.tenant.id ||
      existingTenantContext?.id === response.tenant.id;
    const preservedStoreContext =
      canReuseExistingStoreContext && existingTenantContext
        ? {
            currentStoreId: existingTenantContext.currentStoreId,
            availableStores: existingTenantContext.availableStores,
          }
        : {};

    // Save tokens
    saveEncryptedToken(TOKEN_KEYS.ACCESS_TOKEN, response.access_token);
    saveEncryptedToken(TOKEN_KEYS.REFRESH_TOKEN, response.refresh_token);

    // Save enhanced user data
    saveEncryptedToken(JSON.stringify(response.user), TOKEN_KEYS.USER_DATA);

    // Save tenant context
    const tenantContext: TenantContext = {
      id: response.tenant.id,
      tenantId: response.tenant.id, // Legacy compatibility
      name: response.tenant.name,
      tenantName: response.tenant.name, // Legacy compatibility
      subdomain: response.tenant.subdomain,
      planType: response.tenant.planType,
      features: response.tenant.features,
      billingStatus: response.tenant.billingStatus,
      userRole: response.user.tenantRole,
      tenantRole: response.user.tenantRole, // Legacy compatibility
      permissions: response.user.permissions,
      ...preservedStoreContext,
    };
    saveEncryptedToken(
      JSON.stringify(tenantContext),
      TOKEN_KEYS.TENANT_CONTEXT
    );

    // Save session information
    if (response.session) {
      saveEncryptedToken(
        JSON.stringify(response.session),
        'ripia_session_info'
      );
    }

    // Save security context
    if (response.security) {
      saveEncryptedToken(
        JSON.stringify(response.security),
        'ripia_security_info'
      );
    }

    logger.log('[AuthService] V2 login response saved successfully');
  }

  /**
   * Issue #2353/#3582: tenantContext未設定時にバックエンドから自動取得して保存する。
   * checkAuthState内から呼ばれ、Clerk JWTでGET /shops/myを叩く。
   */
  static async fetchAndSaveTenantContextResult(
    userId: string
  ): Promise<TenantContextFetchResult> {
    // ロールからpermissionsを導出（最小権限の原則）
    // TODO: Extract to shared SSOT (ref: Issue #2239 pattern, plan-comparison.md)
    function derivePermissions(role?: string): string[] {
      const ownerPerms = [
        'organization.manage',
        'organization.settings',
        'members.invite',
        'members.manage',
        'billing.manage',
        'analytics.view',
        'stores.manage',
      ];
      const managerPerms = [
        'organization.settings',
        'members.invite',
        'analytics.view',
        'stores.manage',
      ];
      const staffPerms = ['analytics.view'];
      switch (role) {
        case 'owner':
        case 'tenant_admin':
        case 'super_admin':
          return ownerPerms;
        case 'manager':
          return managerPerms;
        default:
          return staffPerms;
      }
    }

    async function getSessionToken(
      session: ClerkSessionForTenantContext | undefined,
      options?: { skipCache?: boolean }
    ): Promise<TenantContextFetchResult | string> {
      try {
        const token = await session?.getToken(options);
        if (!token) {
          logger.info(
            '[AuthService] tenantContext auto-fetch skipped: no Clerk token'
          );
          return createTenantContextFetchFailure('auth_required');
        }
        return token;
      } catch (err) {
        const errorMsg =
          err instanceof Error ? `${err.name}: ${err.message}` : String(err);
        logger.error('[AuthService] Clerk token read failed:', errorMsg);
        Sentry.captureMessage('AuthService: Clerk token read failed', {
          level: 'warning',
          extra: {
            errorName: getSafeErrorName(err),
          },
        });
        return createTenantContextFetchFailure('transient');
      }
    }

    async function fetchShopWithToken(apiBaseUrl: string, token: string) {
      return fetch(`${apiBaseUrl}/api/v1/shops/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    }

    async function buildTenantContextFromResponse(
      res: Response
    ): Promise<TenantContextFetchResult> {
      const shopData = await res.json();
      // TransformInterceptor envelope unwrap
      const shop = (shopData?.data ?? shopData) as Record<string, unknown>;
      const tenantId = shop?.tenantId;
      const shopId = shop?.id;
      const hasTenantId =
        typeof tenantId === 'string' && tenantId.trim().length > 0;
      const hasShopId =
        typeof shopId === 'string' && shopId.trim().length > 0;

      if (!hasTenantId || !hasShopId) {
        Sentry.captureMessage(
          'AuthService: /shops/my 200 with missing tenant fields',
          {
            level: 'warning',
            extra: {
              hasTenantId,
              hasShopId,
            },
          }
        );
        return createTenantContextFetchFailure('unknown', 200);
      }

      if (!shop.userRole) {
        logger.warn(
          '[AuthService] userRole missing from /shops/my response, defaulting to staff'
        );
      }
      const role =
        typeof shop.userRole === 'string'
          ? (shop.userRole as TenantContext['userRole'])
          : ('staff' as const);
      const shopName = typeof shop.name === 'string' ? shop.name : '店舗';
      const availableStores = [{ id: shopId, name: shopName, code: '' }];
      const newTenantContext: TenantContext = {
        id: tenantId,
        tenantId,
        name: shopName,
        tenantName: shopName,
        subdomain: `salon-${userId.slice(0, 8)}`,
        planType:
          typeof shop.plan === 'string'
            ? (shop.plan as TenantContext['planType'])
            : ('light' as const),
        features: [
          'basic-booking',
          'customer-management',
          'staff-management',
        ],
        billingStatus: 'active' as const,
        userId,
        userRole: role,
        tenantRole: role,
        permissions: derivePermissions(role),
        currentStoreId: shopId,
        availableStores,
      };
      saveEncryptedToken(
        JSON.stringify(newTenantContext),
        TOKEN_KEYS.TENANT_CONTEXT
      );
      // Issue #2448: tenantContext保存後にカスタムイベントで通知 (ADR-0030)
      // Signal-only: データはイベントに載せず、リスナー側でAuthServiceから再読み取り
      try {
        window.dispatchEvent(new Event('ripia:tenantContextUpdated'));
      } catch (eventError) {
        logger.error(
          '[AuthService] tenantContextUpdated event dispatch failed:',
          eventError
        );
        Sentry.captureMessage(
          'AuthService: tenantContextUpdated event dispatch failed',
          {
            level: 'warning',
            extra: {
              errorName: getSafeErrorName(eventError),
            },
          }
        );
      }
      logger.info('[AuthService] tenantContext auto-generated from /shops/my');
      return {
        ok: true,
        tenantContext: newTenantContext,
      };
    }

    function classifyShopResponse(status: number): TenantContextFetchResult {
      if (status === 404) {
        logger.info(
          '[AuthService] /shops/my returned 404 (webhook processing pending), will retry'
        );
        return createTenantContextFetchFailure('setup_pending', status);
      }

      if (status >= 500) {
        logger.error('[AuthService] Failed to fetch /shops/my:', status);
        return createTenantContextFetchFailure('transient', status);
      }

      logger.error('[AuthService] Failed to fetch /shops/my:', status);
      return createTenantContextFetchFailure('unknown', status);
    }

    try {
      const apiBaseUrl = (
        import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'
      ).trim();
      const clerk = (
        window as unknown as {
          Clerk?: {
            session?: ClerkSessionForTenantContext;
          };
        }
      ).Clerk;

      const tokenResult = await getSessionToken(clerk?.session);
      if (typeof tokenResult !== 'string') {
        return tokenResult;
      }

      let res = await fetchShopWithToken(apiBaseUrl, tokenResult);
      if (res.status === 401 || res.status === 403) {
        const freshTokenResult = await getSessionToken(clerk?.session, {
          skipCache: true,
        });
        if (typeof freshTokenResult !== 'string') {
          return createTenantContextFetchFailure('auth_required', res.status);
        }
        res = await fetchShopWithToken(apiBaseUrl, freshTokenResult);
        if (res.status === 401 || res.status === 403) {
          logger.error('[AuthService] /shops/my auth failed:', res.status);
          return createTenantContextFetchFailure('auth_required', res.status);
        }
      }

      if (res.ok) {
        return buildTenantContextFromResponse(res);
      }

      return classifyShopResponse(res.status);
    } catch (err) {
      const errorMsg =
        err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      logger.error('[AuthService] tenantContext auto-fetch error:', errorMsg);
      return createTenantContextFetchFailure('transient');
    }
  }

  static async fetchAndSaveTenantContext(
    userId: string
  ): Promise<TenantContext | null> {
    const result = await this.fetchAndSaveTenantContextResult(userId);
    if (result.ok) {
      return result.tenantContext;
    }
    return null;
  }

  static extractJWTPayload(): EnhancedJWTPayload | null {
    try {
      const token = loadEncryptedToken(TOKEN_KEYS.ACCESS_TOKEN);
      if (!token) return null;

      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload as EnhancedJWTPayload;
    } catch (error) {
      logger.error('Failed to extract JWT payload:', error);
      return null;
    }
  }

  // ====================================
  // MFA（多要素認証）関連メソッド
  // ====================================

  /**
   * MFA有効化
   * @param request MFA有効化リクエスト
   * @returns MFA有効化レスポンス
   */
  static async enableMFA(
    request: MFAEnableRequest
  ): Promise<MFAEnableResponse> {
    try {
      logger.log('🔐 [AuthService] MFA有効化開始:', { userId: request.userId });

      // バリデーション
      if (!request.userId || !request.secretKey || !request.verificationCode) {
        throw new AuthError(
          '必須パラメータが不足しています',
          'VALIDATION_ERROR'
        );
      }

      if (!/^\d{6}$/.test(request.verificationCode)) {
        throw new AuthError(
          '認証コードは6桁の数字で入力してください',
          'INVALID_VERIFICATION_CODE'
        );
      }

      // TOTPコード検証
      const isValidCode = MFAService.validateMFASetup(
        request.secretKey,
        request.verificationCode
      );
      if (!isValidCode) {
        throw new AuthError(
          '認証コードが正しくありません',
          'INVALID_VERIFICATION_CODE'
        );
      }

      // TODO: データベースでMFAを有効化
      // const response = await this.postV2('/auth/mfa/enable', {
      //   userId: request.userId,
      //   secretKey: request.secretKey,
      //   backupCodes: request.backupCodes,
      // });

      // モック実装（実際のAPI実装まで）
      const mockResponse: MFAEnableResponse = {
        success: true,
        message: 'MFAが正常に有効化されました',
        backupCodesCount: request.backupCodes.length,
      };

      logger.log('✅ [AuthService] MFA有効化完了:', {
        userId: request.userId,
        backupCodesCount: mockResponse.backupCodesCount,
      });

      return mockResponse;
    } catch (error) {
      logger.error('❌ [AuthService] MFA有効化エラー:', {
        error,
        userId: request.userId,
      });

      if (error instanceof AuthError) {
        throw error;
      }

      throw new AuthError('MFAの有効化に失敗しました', 'MFA_ENABLE_FAILED');
    }
  }

  /**
   * MFA無効化
   * @param request MFA無効化リクエスト
   * @returns MFA無効化レスポンス
   */
  static async disableMFA(
    request: MFADisableRequest
  ): Promise<MFADisableResponse> {
    try {
      logger.log('🔐 [AuthService] MFA無効化開始:', { userId: request.userId });

      // バリデーション
      if (!request.userId || !request.currentPassword) {
        throw new AuthError(
          '必須パラメータが不足しています',
          'VALIDATION_ERROR'
        );
      }

      if (!request.verificationCode && !request.backupCode) {
        throw new AuthError(
          '認証コードまたはバックアップコードが必要です',
          'VALIDATION_ERROR'
        );
      }

      // TODO: 現在のパスワード確認とMFA認証
      // TODO: データベースでMFAを無効化

      // モック実装（実際のAPI実装まで）
      const mockResponse: MFADisableResponse = {
        success: true,
        message: 'MFAが正常に無効化されました',
      };

      logger.log('✅ [AuthService] MFA無効化完了:', { userId: request.userId });

      return mockResponse;
    } catch (error) {
      logger.error('❌ [AuthService] MFA無効化エラー:', {
        error,
        userId: request.userId,
      });

      if (error instanceof AuthError) {
        throw error;
      }

      throw new AuthError('MFAの無効化に失敗しました', 'MFA_DISABLE_FAILED');
    }
  }

  /**
   * MFA認証
   * @param request MFA認証リクエスト
   * @returns MFA認証レスポンス
   */
  static async verifyMFA(
    request: MFAVerifyRequest
  ): Promise<MFAVerifyResponse> {
    try {
      logger.log('🔐 [AuthService] MFA認証開始:', {
        isBackupCode: request.isBackupCode,
        codeLength: request.code?.length,
      });

      // バリデーション
      if (!request.tempToken || !request.code) {
        throw new AuthError(
          '必須パラメータが不足しています',
          'VALIDATION_ERROR'
        );
      }

      // 一時トークンの検証とデコード
      let tempTokenData: TempAuthToken;
      try {
        // TODO: 実際のJWT検証実装
        // tempTokenData = this.validateTempToken(request.tempToken);

        // モック実装
        tempTokenData = {
          tempToken: request.tempToken,
          expiresAt: Date.now() + 300000, // 5分
          email: 'user@example.com',
          userId: 'user123',
          mfaRequired: true,
        };
      } catch (error) {
        throw new AuthError(
          '認証セッションの有効期限が切れました',
          'TEMP_TOKEN_EXPIRED'
        );
      }

      // MFAコード検証
      let isValidCode = false;
      let backupCodesRemaining: number | undefined;

      if (request.isBackupCode) {
        // バックアップコード検証
        // TODO: データベースからバックアップコードを取得して検証
        // const { isValid, remainingCodes } = await this.verifyBackupCode(tempTokenData.userId, request.code);

        // モック実装
        isValidCode = /^[A-F0-9]{4}-[A-F0-9]{4}$/i.test(request.code);
        backupCodesRemaining = isValidCode ? 8 : undefined;
      } else {
        // TOTPコード検証
        // TODO: データベースからユーザーのMFAシークレットを取得して検証
        // const userMFASecret = await this.getUserMFASecret(tempTokenData.userId);
        // isValidCode = MFAService.verifyTOTP(userMFASecret, request.code);

        // モック実装
        isValidCode = /^\d{6}$/.test(request.code);
      }

      if (!isValidCode) {
        throw new AuthError(
          request.isBackupCode
            ? 'バックアップコードが正しくありません'
            : '認証コードが正しくありません',
          'INVALID_MFA_CODE'
        );
      }

      // 正規のJWTトークン生成
      const jwtPayload: JWTPayload = {
        sub: tempTokenData.userId,
        email: tempTokenData.email,
        role: 'owner', // TODO: 実際のロール情報を設定
        permissions: ['all'], // TODO: 実際の権限情報を設定
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + 24 * 60 * 60, // 24時間
        mfa_enabled: true,
        mfa_verified: true,
        security_level: 'high',
      };

      // TODO: 実際のJWT生成とリフレッシュトークン生成
      const accessToken = this.generateMockJWT(jwtPayload);
      const refreshToken = `refresh_${Date.now()}_${Math.random().toString(36)}`;

      // TODO: データベースからユーザー情報を取得
      const user: User = {
        id: tempTokenData.userId,
        email: tempTokenData.email,
        name: 'Mock User',
        role: 'owner',
        emailVerified: true,
        mfaEnabled: true,
        createdAt: new Date().toISOString(),
      };

      const response: MFAVerifyResponse = {
        success: true,
        message: 'MFA認証が完了しました',
        accessToken,
        refreshToken,
        user,
        backupCodesRemaining,
      };

      logger.log('✅ [AuthService] MFA認証完了:', {
        userId: tempTokenData.userId,
        backupCodesRemaining,
      });

      return response;
    } catch (error) {
      logger.error('❌ [AuthService] MFA認証エラー:', {
        error,
        isBackupCode: request.isBackupCode,
      });

      if (error instanceof AuthError) {
        throw error;
      }

      throw new AuthError('MFA認証に失敗しました', 'MFA_VERIFICATION_FAILED');
    }
  }

  /**
   * MFA状態取得
   * @param request MFA状態リクエスト
   * @returns MFA状態レスポンス
   */
  static async getMFAStatus(
    request: MFAStatusRequest
  ): Promise<MFAStatusResponse> {
    try {
      logger.log('🔐 [AuthService] MFA状態取得開始:', {
        userId: request.userId,
      });

      // TODO: データベースからMFA状態を取得
      // const mfaStatus = await this.getV2(`/auth/mfa/status/${request.userId}`);

      // モック実装
      const mockResponse: MFAStatusResponse = {
        isEnabled: false,
        backupCodesRemaining: 0,
        lastUsed: undefined,
        setupDate: undefined,
      };

      logger.log('✅ [AuthService] MFA状態取得完了:', {
        userId: request.userId,
        isEnabled: mockResponse.isEnabled,
      });

      return mockResponse;
    } catch (error) {
      logger.error('❌ [AuthService] MFA状態取得エラー:', {
        error,
        userId: request.userId,
      });

      throw new AuthError('MFA状態の取得に失敗しました', 'MFA_STATUS_FAILED');
    }
  }

  /**
   * バックアップコード再生成
   * @param request バックアップコード再生成リクエスト
   * @returns バックアップコード再生成レスポンス
   */
  static async generateNewBackupCodes(
    request: MFAGenerateBackupCodesRequest
  ): Promise<MFAGenerateBackupCodesResponse> {
    try {
      logger.log('🔐 [AuthService] バックアップコード再生成開始:', {
        userId: request.userId,
      });

      // バリデーション
      if (!request.userId || !request.currentPassword) {
        throw new AuthError(
          '必須パラメータが不足しています',
          'VALIDATION_ERROR'
        );
      }

      if (!request.verificationCode && !request.backupCode) {
        throw new AuthError(
          '認証コードまたはバックアップコードが必要です',
          'VALIDATION_ERROR'
        );
      }

      // TODO: パスワード確認とMFA認証
      // TODO: 新しいバックアップコード生成とデータベース更新

      const newBackupCodes = MFAService.generateBackupCodes();

      const response: MFAGenerateBackupCodesResponse = {
        success: true,
        backupCodes: newBackupCodes,
        message: '新しいバックアップコードが生成されました',
      };

      logger.log('✅ [AuthService] バックアップコード再生成完了:', {
        userId: request.userId,
        codesCount: newBackupCodes.length,
      });

      return response;
    } catch (error) {
      logger.error('❌ [AuthService] バックアップコード再生成エラー:', {
        error,
        userId: request.userId,
      });

      if (error instanceof AuthError) {
        throw error;
      }

      throw new AuthError(
        'バックアップコードの再生成に失敗しました',
        'BACKUP_CODES_GENERATION_FAILED'
      );
    }
  }
}

export default AuthService;

// エンタープライズセキュリティ機能のエクスポート
export {
  EnhancedJWTService,
  TokenBlacklistService,
  DeviceAuthService,
  MultiTenantStorageService,
};

// ========================================================================================
// Issue #1283: authService分割対応 - 新サービスの再エクスポート
// これらの新サービスは将来的にauthService.tsを置き換えます。
// 新規コードではこれらのサービスを直接使用することを推奨します。
// ========================================================================================

export {
  AuthenticationService,
  TenantService,
  PermissionService,
} from './auth';

// 型定義の再エクスポート（後方互換性）
export type * from './auth/types';

// 開発環境での認証テスト用
if (import.meta.env?.MODE === 'development') {
  (
    window as typeof window & {
      AuthService: typeof AuthService;
      EnhancedJWTService: typeof EnhancedJWTService;
      TokenBlacklistService: typeof TokenBlacklistService;
      DeviceAuthService: typeof DeviceAuthService;
    }
  ).AuthService = AuthService;
  (window as unknown as UnknownObject).EnhancedJWTService = EnhancedJWTService;
  (window as unknown as UnknownObject).TokenBlacklistService =
    TokenBlacklistService;
  (window as unknown as UnknownObject).DeviceAuthService = DeviceAuthService;
}
